import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import { ArrowDownTrayIcon, ArrowUpTrayIcon, XMarkIcon, MagnifyingGlassIcon, BugAntIcon, LightBulbIcon, ViewColumnsIcon, LockClosedIcon, EllipsisVerticalIcon, ChevronDownIcon, ArchiveBoxIcon, ArrowTrendingUpIcon } from '@heroicons/react/24/outline';
import ScrollableDropdown from '../components/ScrollableDropdown';
import MultiSelectColumnFilter from '../components/MultiSelectColumnFilter';
import DateColumnFilter from '../components/DateColumnFilter';

// Helper to extract filters from search params
const getFiltersFromURL = (searchParams) => {
  const filterKeys = ['application', 'module', 'category', 'status', 'severity', 'priority', 'reportedBy', 'domain', 'date', 'assignedTo', 'createdDate', 'updatedDate', 'ticketType'];
  const filters = {};
  filterKeys.forEach(key => {
    const values = searchParams.getAll(key);
    if (values.length === 0) {
      filters[key] = '';
    } else if (values.length === 1) {
      filters[key] = values[0];
    } else {
      filters[key] = values;
    }
  });
  return filters;
};

const BugList = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // State restored and verified at top of component
  const [sortConfig] = useState(() => {
    try {
      const raw = sessionStorage.getItem('bugListState');
      if (raw && raw !== 'undefined') {
        const parsed = JSON.parse(raw);
        return parsed.sortConfig || { key: 'createdAt', direction: 'desc' };
      }
    } catch (e) { }
    return { key: 'createdAt', direction: 'desc' };
  });
  const [showOverflowMenu, setShowOverflowMenu] = useState(false);


  // Load basic state from sessionStorage to ensure consistency on mount
  const savedState = useMemo(() => {
    try {
      const raw = sessionStorage.getItem('bugListState');
      if (raw && raw !== 'undefined') return JSON.parse(raw);
    } catch (e) {
      console.warn('Failed to parse bugListState', e);
    }
    return {};
  }, []);

  // Helper to get initial filters from all possible sources (URL > Location > Session)
  const getInitialFilters = useCallback(() => {
    const urlFilters = getFiltersFromURL(searchParams);
    const hasUrlFilters = Object.values(urlFilters).some(val => val !== '');
    if (hasUrlFilters) return urlFilters;

    if (location.state && !location.state.resetFilters) {
      return {
        application: location.state.selectedApplication || '',
        module: '',
        category: '',
        status: location.state.status || '',
        severity: location.state.severity || '',
        priority: location.state.priority || '',
        reportedBy: location.state.reportedBy || '',
        domain: location.state.domain || '',
        date: '',
        ticketType: location.state.ticketType || ''
      };
    }

    // Default: start with no filters (temporary KPI filters should not persist)
    return {
      application: '',
      module: '',
      category: '',
      status: '',
      severity: '',
      priority: '',
      reportedBy: '',
      domain: '',
      date: '',
      assignedTo: '',
      createdDate: '',
      updatedDate: '',
      ticketType: ''
    };
  }, [searchParams, location.state]);

  const getInitialSearchTerm = useCallback(() => {
    if (searchParams.has('search')) return searchParams.get('search');
    return savedState.searchTerm || '';
  }, [searchParams, savedState.searchTerm]);

  const initialFilters = useMemo(() => getInitialFilters(), [getInitialFilters]);
  const initialSearch = useMemo(() => getInitialSearchTerm(), [getInitialSearchTerm]);

  // Unified state for filters and search to ensure they are set together from Render #1
  const [filters, setFilters] = useState(initialFilters);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [activeSearchTerm, setActiveSearchTerm] = useState(initialSearch);
  const hasActiveFilters = useMemo(() => {
    const anyFilter =
      Object.values(filters).some(v => Array.isArray(v) ? v.length > 0 : !!v) ||
      !!activeSearchTerm;
    return anyFilter;
  }, [filters, activeSearchTerm]);

  const [viewMode, setViewMode] = useState(() => {
    // Priority 1: URL param ?view=archived from KPI card navigation
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('view') === 'archived') {
      sessionStorage.setItem('bugListViewMode', 'archived');
      return 'archived';
    }
    // Priority 2: sessionStorage (persists across navigation within the app)
    return sessionStorage.getItem('bugListViewMode') || 'active';
  }); // 'active' or 'archived'

  useEffect(() => {
    sessionStorage.setItem('bugListViewMode', viewMode);
  }, [viewMode]);

  const [typeFilter, setTypeFilter] = useState(['All']);

  // Initialize data states - ONLY trust cache if it matches the current initial filters
  const [bugs, setBugs] = useState(() => {
    try {
      const currentViewMode = sessionStorage.getItem('bugListViewMode') || 'active';
      const cacheKey = `bugListResults_${currentViewMode}`;
      const cachedData = sessionStorage.getItem(cacheKey);
      
      if (cachedData && savedState.filters) {
        const filtersMatch = JSON.stringify(savedState.filters) === JSON.stringify(initialFilters);
        const searchMatch = (savedState.searchTerm || '') === initialSearch;

        if (filtersMatch && searchMatch) {
          return JSON.parse(cachedData);
        }
      }
    } catch (e) { }
    return [];
  });

  const [loading, setLoading] = useState(() => {
    return bugs.length === 0;
  });

  const [applications, setApplications] = useState(() => {
    try {
      const cached = sessionStorage.getItem('bugListApps');
      return cached ? JSON.parse(cached) : [];
    } catch (e) { return []; }
  });

  const [reporters, setReporters] = useState(() => {
    try {
      const cached = sessionStorage.getItem('bugListReporters');
      return cached ? JSON.parse(cached) : [];
    } catch (e) { return []; }
  });

  // Users who have actually reported bugs (for the "Reported By" filter), keyed by view mode
  const [bugReporters, setBugReporters] = useState(() => {
    try {
      const cached = sessionStorage.getItem(`bugListActiveReporters_${viewMode}`);
      return cached ? JSON.parse(cached) : [];
    } catch (e) { return []; }
  });

  const updateBugReporters = useCallback((list) => {
    if (!Array.isArray(list)) return;
    setBugReporters(list);
    try {
      sessionStorage.setItem(`bugListActiveReporters_${viewMode}`, JSON.stringify(list));
    } catch (e) { /* ignore storage errors */ }
  }, [viewMode]);

  const getInitialPage = useCallback(() => {
    if (searchParams.has('page')) return Number(searchParams.get('page') || '1') || 1;
    if (location.state && !location.state.resetFilters) return 1;
    
    // Only trust savedState if the viewMode hasn't changed
    const savedViewMode = sessionStorage.getItem('bugListViewMode') || 'active';
    if (savedViewMode === viewMode) {
      return savedState.currentPage || 1;
    }
    return 1;
  }, [searchParams, location.state, savedState.currentPage, viewMode]);

  const [currentPage, setCurrentPage] = useState(getInitialPage());
  const [pageSize, setPageSize] = useState(savedState.pageSize || 10);
  const [totalCount, setTotalCount] = useState(savedState.totalCount || 0);
  const [totalPages, setTotalPages] = useState(savedState.totalPages || 0);
  // sortConfig moved to top of component


  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importDefaultApplication, setImportDefaultApplication] = useState(initialFilters.application || '');
  const [importing, setImporting] = useState(false);
  const [importAssignee, setImportAssignee] = useState('');
  const [importAssigneeOptions, setImportAssigneeOptions] = useState([]);
  const [importFieldErrors, setImportFieldErrors] = useState({ application: '', assignee: '', file: '' });
  const [showTemplateInfo, setShowTemplateInfo] = useState(false);

  const [domainList, setDomainList] = useState([]);
  const fileInputRef = useRef(null);

  const [inlineUpdating, setInlineUpdating] = useState({});
  const [activeInlineEditor, setActiveInlineEditor] = useState(null);

  // Close Bug Modal state (for inline status change in Bug List)
  const [showCloseBugModalList, setShowCloseBugModalList] = useState(false);
  const [closeBugVerificationLinkList, setCloseBugVerificationLinkList] = useState('');
  const [closeBugVerificationNotesList, setCloseBugVerificationNotesList] = useState('');
  const [pendingCloseBug, setPendingCloseBug] = useState(null);
  const [pendingVerificationStatusList, setPendingVerificationStatusList] = useState('Closed');
  
  // Export Column Management
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportColumns, setExportColumns] = useState([]);

  // Column Management State
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  // showOverflowMenu moved to top of component

  const [columnSearchTerm, setColumnSearchTerm] = useState('');

  // Removed: activeFilterCount (unused)

  // Manage body overflow for modals to prevent layout/scrollbar shifts
  useEffect(() => {
    if (showColumnSelector || showImportModal) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [showColumnSelector, showImportModal]);

  // Drag and Drop State
  const [draggedItem, setDraggedItem] = useState(null);
  const isInitialized = useRef(false);
  const [columnOrder, setColumnOrder] = useState(() => {
    const saved = localStorage.getItem('bugListColumnOrder');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.warn('Failed to parse saved column order', e);
      }
    }
    return null; // Will use default order from allColumns
  });

  // Define all available columns with metadata
  const allColumns = useMemo(() => [
    { id: 'bugId', label: 'Bug ID', locked: true, defaultVisible: true },
    { id: 'title', label: 'Title', locked: true, defaultVisible: true },
    { id: 'reportedBy', label: 'Reported By', locked: false, defaultVisible: false },
    { id: 'application', label: 'Application Module', locked: false, defaultVisible: true },
    { id: 'domain', label: 'Domain', locked: false, defaultVisible: false },
    { id: 'category', label: 'Category', locked: false, defaultVisible: false },
    { id: 'status', label: 'Status', locked: false, defaultVisible: true },
    { id: 'severity', label: 'Severity', locked: false, defaultVisible: false },
    { id: 'priority', label: 'Priority', locked: false, defaultVisible: true },
    { id: 'assignedTo', label: 'Assigned To', locked: false, defaultVisible: true },
    { id: 'createdDate', label: 'Created Date', locked: false, defaultVisible: true },
    { id: 'updatedDate', label: 'Updated Date', locked: false, defaultVisible: true },
    { id: 'archivedAt', label: 'Archived Date', locked: false, defaultVisible: false },
    { id: 'archivedBy', label: 'Archived By', locked: false, defaultVisible: false },
    { id: 'actions', label: 'Actions', locked: false, defaultVisible: true, nonExportable: true },
    // Export-only columns (not shown in table but available in export modal)
    { id: 'description', label: 'Description', locked: false, defaultVisible: false, exportOnly: true },
    { id: 'stepsToReproduce', label: 'Steps to Reproduce', locked: false, defaultVisible: false, exportOnly: true },
    { id: 'expectedResult', label: 'Expected Result', locked: false, defaultVisible: false, exportOnly: true },
    { id: 'actualResult', label: 'Actual Result', locked: false, defaultVisible: false, exportOnly: true },
    { id: 'environment', label: 'Environment', locked: false, defaultVisible: false, exportOnly: true },
    { id: 'reportedByEmail', label: 'Reporter Email', locked: false, defaultVisible: false, exportOnly: true },
    { id: 'assignedToEmail', label: 'Assignee Email', locked: false, defaultVisible: false, exportOnly: true },
    { id: 'attachmentUrls', label: 'Attachment URLs', locked: false, defaultVisible: false, exportOnly: true },
  ], []);

  // Initialize visible columns from localStorage or defaults
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('bugListVisibleColumns');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.warn('Failed to parse saved columns', e);
      }
    }
    return allColumns.filter(col => col.defaultVisible).map(col => col.id);
  });

  // Save visible columns to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('bugListVisibleColumns', JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  // Save column order to localStorage whenever it changes
  useEffect(() => {
    if (columnOrder) {
      localStorage.setItem('bugListColumnOrder', JSON.stringify(columnOrder));
    }
  }, [columnOrder]);

  // Adjust visible columns based on viewMode
  useEffect(() => {
    if (viewMode === 'archived') {
      setVisibleColumns(prev => {
        const next = [...prev];
        if (!next.includes('archivedAt')) next.push('archivedAt');
        if (!next.includes('archivedBy')) next.push('archivedBy');
        // Optionally hide status as it is less relevant for archived bugs
        return next;
      });
    } else {
      // If switching back to active, we could optionally hide archive columns, 
      // but let's leave them if the user explicitly enabled them.
    }
  }, [viewMode]);

  // Get ordered columns based on saved order or default
  const orderedColumns = useMemo(() => {
    if (!columnOrder || columnOrder.length === 0) {
      return allColumns;
    }

    // Create a map for quick lookup
    const columnMap = new Map(allColumns.map(col => [col.id, col]));

    // Order columns based on saved order
    const ordered = columnOrder
      .map(id => columnMap.get(id))
      .filter(Boolean); // Remove any undefined entries

    // Add any new columns that aren't in the saved order
    const orderedIds = new Set(columnOrder);
    const newColumns = allColumns.filter(col => !orderedIds.has(col.id));

    return [...ordered, ...newColumns];
  }, [allColumns, columnOrder]);
    
  // Filtered columns for selector (with search) — exclude exportOnly columns
  const filteredColumnsForSelector = useMemo(() => {
    const searchLower = columnSearchTerm.toLowerCase();
    return orderedColumns.filter(col =>
      !col.exportOnly &&
      col.label.toLowerCase().includes(searchLower)
    );
  }, [orderedColumns, columnSearchTerm]);

  // Column management functions
  const isColumnVisible = useCallback((columnId) => {
    return visibleColumns.includes(columnId);
  }, [visibleColumns]);

  const toggleColumn = useCallback((columnId) => {
    setVisibleColumns(prev => {
      if (prev.includes(columnId)) {
        return prev.filter(id => id !== columnId);
      } else {
        return [...prev, columnId];
      }
    });
  }, []);

  const selectAllColumns = useCallback(() => {
    const filteredIds = filteredColumnsForSelector.map(col => col.id);
    setVisibleColumns(prevVisible => {
      const nextVisible = new Set([...prevVisible, ...filteredIds]);
      return Array.from(nextVisible);
    });
  }, [filteredColumnsForSelector]);



  const resetToDefaultColumns = useCallback(() => {
    setVisibleColumns(allColumns.filter(col => col.defaultVisible).map(col => col.id));
    setColumnOrder(null);
    localStorage.removeItem('bugListColumnOrder');
  }, [allColumns]);

  // Consolidated URL synchronization (State -> URL)
  // We debounce this to avoid rapid URL updates that can trigger browser extension errors (like Dashlane/LastPass message channel errors)
  useEffect(() => {
    const timer = setTimeout(() => {
      const newParams = new URLSearchParams(searchParams);

      // List of all keys we manage

      // Determine if anything meaningful changed
      let hasChanged = false;

      // 1. Check Search term
      const urlSearch = searchParams.get('search') || '';
      if (searchTerm !== urlSearch) {
        if (searchTerm) newParams.set('search', searchTerm);
        else newParams.delete('search');
        hasChanged = true;
      }

      // 2. Check Page
      const urlPage = searchParams.get('page') || '1';
      if (currentPage.toString() !== urlPage) {
        newParams.set('page', currentPage.toString());
        hasChanged = true;
      }

      // 3. Check Filters
      Object.keys(filters).forEach(key => {
        const urlValues = searchParams.getAll(key);
        const stateValue = filters[key];

        if (Array.isArray(stateValue)) {
          const stateValuesStr = [...stateValue].sort().join(',');
          const urlValuesStr = [...urlValues].sort().join(',');
          if (stateValuesStr !== urlValuesStr) {
            newParams.delete(key);
            stateValue.forEach(v => { if (v) newParams.append(key, v); });
            hasChanged = true;
          }
        } else {
          const urlValue = searchParams.get(key) || '';
          if (stateValue !== urlValue) {
            if (stateValue) newParams.set(key, stateValue);
            else newParams.delete(key);
            hasChanged = true;
          }
        }
      });

      // Synchronize with activeSearchTerm for the main view
      if (activeSearchTerm !== searchTerm) {
        setActiveSearchTerm(searchTerm);
      }

      if (hasChanged) {
        setSearchParams(newParams, { replace: true });
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [filters, searchTerm, currentPage, searchParams, setSearchParams, activeSearchTerm]);

  // Sync state from URL changes
  useEffect(() => {
    // Only sync FROM URL if we are initialized OR the URL has content.
    // This prevents an empty URL from clearing our restored state during initial mount logic.
    const urlFilters = getFiltersFromURL(searchParams);
    const urlSearch = searchParams.get('search') || '';
    const urlPage = Number(searchParams.get('page') || '1');

    const hasUrlParams = searchParams.has('page') || Object.keys(urlFilters).some(key => searchParams.has(key)) || searchParams.has('search');

    if (!hasUrlParams && !isInitialized.current) {
      return;
    }

    // Update filters if changed
    let filtersChanged = false;
    const newFilters = { ...filters };
    Object.keys(urlFilters).forEach(key => {
      const urlVal = urlFilters[key];
      const stateVal = filters[key];

      const urlStr = Array.isArray(urlVal) ? [...urlVal].sort().join(',') : (urlVal || '');
      const stateStr = Array.isArray(stateVal) ? [...stateVal].sort().join(',') : (stateVal || '');

      if (urlStr !== stateStr) {
        newFilters[key] = urlVal;
        filtersChanged = true;
      }
    });

    if (filtersChanged) {
      setFilters(newFilters);
      setImportDefaultApplication(newFilters.application);
    }

    // Update activeSearchTerm if changed
    if (activeSearchTerm !== urlSearch) {
      setActiveSearchTerm(urlSearch);
      setSearchTerm(urlSearch);
    }

    // Update currentPage if changed from URL (like browser back/forward)
    if (currentPage !== urlPage && urlPage > 0) {
      setCurrentPage(urlPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Save filter/page/sort state to sessionStorage explicitly whenever they change
  useEffect(() => {
    if (!isInitialized.current) return;

    const stateToSave = {
      filters, // This will now come from URL state
      currentPage,
      pageSize,
      totalCount,
      totalPages,
      sortConfig,
      // We don't save search term here if it's in URL, but we can save it for restoration if needed
      searchTerm: activeSearchTerm,
      // Maintain the existing scrollPos or default to 0 to avoid overwriting it with 0 if we haven't scrolled yet
      scrollPos: JSON.parse(sessionStorage.getItem('bugListState') || '{}').scrollPos || 0
    };
    sessionStorage.setItem('bugListState', JSON.stringify(stateToSave));
  }, [filters, currentPage, pageSize, sortConfig, activeSearchTerm, totalCount, totalPages]);

  // Save scroll position separately on scroll event
  useEffect(() => {
    const handleScroll = () => {
      const currentState = JSON.parse(sessionStorage.getItem('bugListState') || '{}');
      sessionStorage.setItem('bugListState', JSON.stringify({
        ...currentState,
        scrollPos: window.scrollY
      }));
    };

    // Debounce scroll save slightly to avoid excessive writes
    let timeoutId;
    const debouncedScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleScroll, 100);
    };

    window.addEventListener('scroll', debouncedScroll);
    return () => {
      window.removeEventListener('scroll', debouncedScroll);
      clearTimeout(timeoutId);
    };
  }, []);

  // Restore scroll position after loading
  useEffect(() => {
    if (!loading && savedState.scrollPos) {
      window.scrollTo(0, savedState.scrollPos);
    }
  }, [loading, savedState.scrollPos]);

  const handleArchive = async (bugId) => {
    if (!window.confirm('Are you sure you want to archive this bug? It will be removed from the active list.')) {
      return;
    }
    try {
      await axios.put(`/api/bugs/${bugId}/archive`);
      toast.success('Bug archived successfully');
      setBugs(prev => prev.filter(b => b._id !== bugId));
    } catch (error) {
      console.error('Archive error:', error);
      toast.error(error.response?.data?.message || 'Failed to archive bug');
    }
  };

  const handleRestore = async (bugId) => {
    if (!window.confirm('Are you sure you want to restore this bug to the active list?')) {
      return;
    }
    try {
      await axios.put(`/api/bugs/${bugId}/restore`);
      toast.success('Bug restored successfully');
      setBugs(prev => prev.filter(b => b._id !== bugId));
    } catch (error) {
      console.error('Restore error:', error);
      toast.error(error.response?.data?.message || 'Failed to restore bug');
    }
  };

  const handleDelete = async (bugId) => {
    if (!window.confirm('Are you sure you want to permanently delete this bug? This action cannot be undone.')) {
      return;
    }
    try {
      await axios.delete(`/api/bugs/${bugId}`);
      toast.success('Bug deleted permanently');
      setBugs(prev => prev.filter(b => b._id !== bugId));
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.message || 'Failed to delete bug');
    }
  };

  const fetchBugs = useCallback(async (page = 1, size = pageSize, showLoader = false) => {
    if (showLoader) {
      setLoading(true);
      setBugs([]);
    }
    try {
      const endpoint = viewMode === 'archived' ? '/api/bugs/archived' : '/api/bugs';
      const queryParams = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (Array.isArray(filters[key])) {
          filters[key].forEach(val => {
            if (val) queryParams.append(key, val);
          });
        } else if (filters[key]) {
          queryParams.append(key, filters[key]);
        }
      });
      queryParams.append('page', page);
      queryParams.append('pageSize', size);
      queryParams.append('sortBy', sortConfig.key);
      queryParams.append('sortOrder', sortConfig.direction);
      if (activeSearchTerm) queryParams.append('bugId', activeSearchTerm);

      const { data } = await axios.get(`${endpoint}?${queryParams.toString()}`);

      // Update states
      setBugs(data.data);
      setTotalCount(data.totalCount);
      setTotalPages(data.totalPages);
      setCurrentPage(data.page);
      updateBugReporters(data.activeReporters);

      if (data.totalPages > 0 && page > data.totalPages) {
        const clampedPage = 1;
        const newParams = new URLSearchParams(searchParams);
        newParams.set('page', String(clampedPage));
        setSearchParams(newParams, { replace: true });
        await fetchBugs(clampedPage, size);
        return;
      }


      // Cache the results and current page for instant loading next time
      const cacheKey = `bugListResults_${viewMode}`;
      sessionStorage.setItem(cacheKey, JSON.stringify(data.data));
      
      const existingState = JSON.parse(sessionStorage.getItem('bugListState') || '{}');
      sessionStorage.setItem('bugListState', JSON.stringify({
        ...existingState,
        currentPage: data.page,
        pageSize: size,
        viewMode: viewMode // Sync viewMode in state cache
      }));
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        toast.error('Network Error: Unable to connect to server. Please check your connection.');
      } else {
        console.error('Failed to fetch bugs:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [filters, pageSize, sortConfig, activeSearchTerm, searchParams, setSearchParams, viewMode, updateBugReporters]);

  useEffect(() => {
    // On mount or filter/search/sort change, fetch data.
    // We intentionally don't put searchParams in dependencies here to avoid the pagination race condition.
    // fetchBugs already takes care of reading searchParams if needed, or we just use current state.

    // Determine the page to fetch: URL > state > 1
    const p = Number(searchParams.get('page')) || currentPage || 1;
    fetchBugs(p, pageSize, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pageSize, sortConfig, activeSearchTerm, viewMode]);

  const fetchReporters = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/auth/all-users');
      // Sort reporters: Alphabetically by name (no "Me" first)
      const sortedReporters = data.data.sort((a, b) => {
        return (a.name || '').localeCompare(b.name || '');
      });
      setReporters(sortedReporters);
      sessionStorage.setItem('bugListReporters', JSON.stringify(sortedReporters));
    } catch (error) {
      console.error('Failed to fetch reporters:', error);
    }
  }, []);

  const fetchBugsWithParams = useCallback(async (paramsObj, showLoader = false) => {
    if (showLoader) {
      setLoading(true);
      setBugs([]);
    }
    try {
      const endpoint = viewMode === 'archived' ? '/api/bugs/archived' : '/api/bugs';
      const newParams = new URLSearchParams(paramsObj);
      const { data } = await axios.get(`${endpoint}?${newParams.toString()}`);
      setBugs(data.data);
      setTotalCount(data.totalCount);
      setTotalPages(data.totalPages);
      setCurrentPage(data.page);
      updateBugReporters(data.activeReporters);
      const cacheKey = `bugListResults_${viewMode}`;
      sessionStorage.setItem(cacheKey, JSON.stringify(data.data));
      const existingState = JSON.parse(sessionStorage.getItem('bugListState') || '{}');
      sessionStorage.setItem('bugListState', JSON.stringify({
        ...existingState,
        currentPage: data.page,
        pageSize
      }));
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        toast.error('Network Error: Unable to connect to server. Please check your connection.');
      } else {
        console.error('Failed to fetch bugs:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [pageSize, viewMode, updateBugReporters]);
  useEffect(() => {
    const fetchDomainsForApps = async (appIds) => {
      try {
        if (!appIds || appIds.length === 0) {
          setDomainList([]);
          return;
        }

        // Fetch domains for all provided application IDs
        const promises = appIds.map(id => 
          axios.get(`/api/applications/${id}/domains`).catch(err => {
            console.error(`Error fetching domains for app ${id}:`, err);
            return { data: { data: [] } };
          })
        );
        
        const results = await Promise.all(promises);
        const allDomains = results.flatMap(res => res.data?.data || []).filter(d => d.status === 'Active');
        
        // Ensure unique domains and sort alphabetically
        const uniqueDomains = Array.from(new Map(allDomains.map(d => [d._id, d])).values())
                                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        
        setDomainList(uniqueDomains);
      } catch (error) {
        console.error('Failed to fetch domains:', error);
      }
    };

    let targetAppIds = [];
    if (filters.application && (Array.isArray(filters.application) ? filters.application.length > 0 : true)) {
      targetAppIds = Array.isArray(filters.application) ? filters.application : [filters.application];
    } else if (applications && applications.length > 0) {
      // If no specific application is selected, show domains from all accessible applications
      targetAppIds = applications.map(app => app._id);
    }

    if (targetAppIds.length > 0) {
      fetchDomainsForApps(targetAppIds);
    } else {
      setDomainList([]);
    }
  }, [filters.application, applications]);


  const fetchApplications = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/applications');
      setApplications(data.data);
      sessionStorage.setItem('bugListApps', JSON.stringify(data.data));
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    }
  }, []);

  useEffect(() => {
    fetchApplications();
    fetchReporters();
  }, [fetchApplications, fetchReporters]);

  // Sync URL to match State on Mount
  useEffect(() => {
    // If URL is empty but state is populated, sync URL
    const isUrlEmpty = !Array.from(searchParams.keys()).some(k =>
      ['application', 'module', 'category', 'status', 'severity', 'priority', 'reportedBy', 'search', 'ticketType'].includes(k)
    );

    if (isUrlEmpty) {
      const newParams = new URLSearchParams();
      let hasUpdates = false;

      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
          hasUpdates = true;
        }
      });

      if (activeSearchTerm) {
        newParams.set('search', activeSearchTerm);
        hasUpdates = true;
      }

      if (hasUpdates) {
        setSearchParams(newParams, { replace: true });
      }
    }

    isInitialized.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount to handle initialization

  // Default Reported By explicitly removed


  // Auto-refresh bugs every 10 seconds for real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      fetchBugs(currentPage, pageSize, false);
    }, 10000); // 10 seconds

    return () => clearInterval(interval);
  }, [fetchBugs, currentPage, pageSize, activeSearchTerm]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const newParams = new URLSearchParams(searchParams);

    setLoading(true);
    setBugs([]);
    setTotalCount(0);
    setTotalPages(0);

    // Update local filters immediately to avoid first-attempt race with URL sync
    setFilters(prev => {
      const next = { ...prev };
      if (Array.isArray(value)) {
        next[name] = value;
      } else {
        next[name] = value || '';
      }
      if (name === 'application') {
        next.domain = '';
        setImportDefaultApplication(next.application || '');
      }
      return next;
    });

    if (Array.isArray(value)) {
      newParams.delete(name);
      value.forEach(v => {
        if (v) newParams.append(name, v);
      });
    } else if (value || name === 'reportedBy') {
      newParams.set(name, value);
    } else {
      newParams.delete(name);
    }

    // Clear domain when application changes
    if (name === 'application') {
      newParams.delete('domain');
    }

    // Reset to page 1 for any filter change
    newParams.set('page', '1');
    setCurrentPage(1);

    setSearchParams(newParams);

    // Fetch immediately with new filters at page 1
    const immediateParams = new URLSearchParams(newParams);
    immediateParams.set('page', '1');
    immediateParams.set('pageSize', String(pageSize));
    immediateParams.set('sortBy', sortConfig.key);
    immediateParams.set('sortOrder', sortConfig.direction);
    if (activeSearchTerm) immediateParams.set('bugId', activeSearchTerm);
    fetchBugsWithParams(immediateParams);
  };

  // Keep Type dropdown in sync with category filter
  useEffect(() => {
    const tType = filters.ticketType;
    if (!tType || (Array.isArray(tType) && tType.length === 0)) {
      setTypeFilter(['All']);
      return;
    }
    if (Array.isArray(tType)) {
      setTypeFilter(tType);
    } else {
      setTypeFilter([tType]);
    }
  }, [filters.ticketType]);

  const handleTypeChange = (values) => {
    setTypeFilter(values);
    if (values.includes('All')) {
      handleFilterChange({ target: { name: 'ticketType', value: '' } });
    } else {
      handleFilterChange({ target: { name: 'ticketType', value: values } });
    }
  };

  const getColumnFilterOptions = (columnId) => {
    switch (columnId) {
      case 'status':
        return [
          { value: 'Open', label: 'Open' },
          { value: 'In Progress', label: 'Dev In Progress' },
          { value: 'Fixed', label: 'Fixed' },
          { value: 'On Hold', label: 'On Hold' },
          { value: 'Retesting In Progress', label: 'QA In Progress' },
          { value: 'Closed', label: 'Closed' },
          { value: 'Reopen', label: 'Reopen' },
          { value: 'Recurring Issue', label: 'Recurring Issue' },
          { value: 'Duplicate', label: 'Duplicate' },
          { value: 'Rejected', label: 'Rejected' },
          { value: 'Deferred', label: 'Deferred' },
          { value: 'Not a Bug', label: 'Not a Bug' },
          { value: 'Deprecated', label: 'Deprecated' }
        ];
      case 'severity':
        return [
          { value: 'Low', label: 'Low' },
          { value: 'Medium', label: 'Medium' },
          { value: 'High', label: 'High' },
          { value: 'Critical', label: 'Critical' }
        ];
      case 'priority':
        return [
          { value: 'Low', label: 'Low' },
          { value: 'Medium', label: 'Medium' },
          { value: 'High', label: 'High' }
        ];
      case 'category': {
        const standardCategories = ['Functional', 'UI/UX', 'Performance', 'Security', 'Integration', 'Data', 'Observation', 'Other'];
        const existingCategories = [...new Set(bugs.map(b => b.category).filter(Boolean))];
        const allCategories = [...new Set([...standardCategories, ...existingCategories])];
        return allCategories
          .sort((a, b) => a.localeCompare(b))
          .map(cat => ({ value: cat, label: cat }));
      }
      case 'ticketType':
        return [
          { value: 'Bug', label: 'Bug' },
          { value: 'Observation', label: 'Observation' },
          { value: 'Improvement', label: 'Improvement' }
        ];
      case 'reportedBy':
        return bugReporters.map(r => ({ value: r._id, label: r.name }));
      case 'assignedTo':
        // Filter transporters/reporters who have bugs or are in app teams
        // For simplicity, using reporters (who are devs)
        return reporters.filter(r => r.role === 'developer').map(r => ({ value: r._id, label: r.name }));
      case 'application':
        return applications.map(a => ({ value: a._id, label: a.name }));
      case 'domain':
        return domainList.map(d => ({ value: d._id, label: d.name }));
      default:
        return [];
    }
  };

  // Helper function to set date range filters (both fromDate and toDate simultaneously) (REMOVED: Single date filter used now)
  /* 
  const setDateRangeFilter = (fromDate, toDate) => {
    const newParams = new URLSearchParams(searchParams);

    if (fromDate) {
      newParams.set('fromDate', fromDate);
    } else {
      newParams.delete('fromDate');
    }

    if (toDate) {
      newParams.set('toDate', toDate);
    } else {
      newParams.delete('toDate');
    }

    // Reset to page 1
    newParams.set('page', '1');
    setCurrentPage(1);

    setSearchParams(newParams);
  };
  */

  const clearFilters = (suppressFetch = false) => {
    // Reset local state immediately to avoid URL sync restoring old values
    const emptyFilters = {
      application: '',
      module: '',
      category: '',
      status: '',
      severity: '',
      priority: '',
      reportedBy: '',
      domain: '',
      date: '',
      assignedTo: '',
      createdDate: '',
      updatedDate: '',
      ticketType: ''
    };
    setFilters(emptyFilters);
    setImportDefaultApplication('');
    setTypeFilter('All');
    setSearchTerm('');
    setActiveSearchTerm('');
    setCurrentPage(1);
    setLoading(true);
    setBugs([]);
    setTotalCount(0);
    setTotalPages(0);

    // Reset URL params (keep page=1)
    const params = new URLSearchParams();
    params.set('page', '1');
    setSearchParams(params, { replace: true });

    // Clear saved scroll position when resetting view
    const savedState = JSON.parse(sessionStorage.getItem('bugListState') || '{}');
    sessionStorage.setItem('bugListState', JSON.stringify({ ...savedState, scrollPos: 0, currentPage: 1 }));
    window.scrollTo(0, 0);
    if (suppressFetch !== true) {
      const immediateParams = new URLSearchParams();
      immediateParams.set('page', '1');
      immediateParams.set('pageSize', String(pageSize));
      immediateParams.set('sortBy', sortConfig.key);
      immediateParams.set('sortOrder', sortConfig.direction);
      fetchBugsWithParams(immediateParams, true);
    }
  };

  const openExport = (format = 'csv') => {
    // Build initial list from current visible columns
    const initialColumns = allColumns
      .filter(col => !col.nonExportable)
      .filter(col => visibleColumns.includes(col.id))
      .map(col => col.id);
    
    setExportColumns(initialColumns);
    setShowExportModal(format); // Use format as the 'open' state value
  };

  const exportBugs = async (format = 'csv', selectedColumns = []) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          if (Array.isArray(filters[key])) {
            filters[key].forEach(val => queryParams.append(key, val));
          } else {
            queryParams.append(key, filters[key]);
          }
        }
      });

      // Include current sorting
      queryParams.append('sortBy', sortConfig.key);
      queryParams.append('sortOrder', sortConfig.direction);
      queryParams.append('format', format);

      // Add selected columns if any
      if (selectedColumns && selectedColumns.length > 0) {
        queryParams.append('columns', selectedColumns.join(','));
      }

      if (activeSearchTerm) queryParams.append('bugId', activeSearchTerm);

      const response = await axios.get(`/api/bugs/export/excel?${queryParams.toString()}`, {
        responseType: 'blob'
      });

      const extension = format === 'csv' ? 'csv' : 'xlsx';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bugs-export-${new Date().toISOString().split('T')[0]}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Bugs exported successfully as ${extension.toUpperCase()}`);
      setShowExportModal(false);
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        toast.error('Network Error: Unable to connect to server. Please check your connection.');
      } else {
        toast.error('Failed to export bugs');
      }
    }
  };

  const openImport = () => {
    setImportDefaultApplication(filters.application || '');
    setImportFile(null);
    setImportAssignee('');
    setImportAssigneeOptions([]);
    setImportFieldErrors({ application: '', assignee: '', file: '' });
    setShowTemplateInfo(false);
    setShowImportModal(true);
  };

  const importFromCsv = async () => {
    if (!importDefaultApplication) {
      setImportFieldErrors(prev => ({ ...prev, application: 'Application is required' }));
      toast.error('Please select an application');
      return;
    } else {
      setImportFieldErrors(prev => ({ ...prev, application: '' }));
    }
    if (user.role === 'tester' && !importAssignee) {
      setImportFieldErrors(prev => ({ ...prev, assignee: 'Assignee (Developer) is required' }));
      toast.error('Please select an Assignee (Developer)');
      return;
    } else {
      setImportFieldErrors(prev => ({ ...prev, assignee: '' }));
    }
    if (!importFile) {
      setImportFieldErrors(prev => ({ ...prev, file: 'Please choose a file (.csv/.xlsx/.xls)' }));
      toast.error('Please choose a file (.csv/.xlsx/.xls)');
      return;
    } else {
      setImportFieldErrors(prev => ({ ...prev, file: '' }));
    }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      fd.append('defaultApplication', importDefaultApplication);
      if (importAssignee) {
        fd.append('defaultAssignee', importAssignee);
      }
      const { data } = await axios.post('/api/bugs/import/csv', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const createdCount = data?.data?.createdCount ?? 0;
      const errorCount = data?.data?.errorCount ?? 0;
      toast.success(`Imported ${createdCount} bugs${errorCount ? ` (${errorCount} errors)` : ''}`);

      setShowImportModal(false);
      sessionStorage.removeItem('bugListResults');
      fetchBugs(1, pageSize);
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        toast.error('Network Error: Unable to connect to server. Please check your connection.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to import bugs');
      }
    } finally {
      setImporting(false);
    }
  };

  useEffect(() => {
    if (!importDefaultApplication) {
      setImportAssigneeOptions([]);
      setImportAssignee('');
      return;
    }
    const app = applications.find(a => String(a._id) === String(importDefaultApplication));
    if (!app) {
      setImportAssigneeOptions([]);
      setImportAssignee('');
      return;
    }
    const devIds = new Set();
    const addId = (val) => {
      if (!val) return;
      const id = val._id || val.id || val;
      if (id) devIds.add(String(id));
    };
    addId(app.developerId);
    addId(app.ownerId);
    if (Array.isArray(app.teamMembers)) {
      app.teamMembers.forEach(m => {
        const role = m.role || (m.userId && m.userId.role);
        if (role === 'DEV' || role === 'developer') {
          addId(m.userId);
        }
      });
    }
    const options = reporters
      .filter(r => r.role === 'developer' && devIds.has(String(r._id)))
      .map(r => ({ value: r._id, label: r.name }));
    setImportAssigneeOptions(options);
    if (options.length === 0) {
      setImportAssignee('');
    } else if (importAssignee && !options.some(o => o.value === importAssignee)) {
      setImportAssignee('');
    }
  }, [importDefaultApplication, applications, reporters, importAssignee]);

  const downloadSampleCsv = () => {
    const header = [
      'Bug Title',
      'Description',
      'Bug/Observation',
      'Application',
      'Assigned To',
      'Module',
      'Category',
      'Status',
      'Severity',
      'Priority',
      'Environment',
      'Steps to Reproduce',
      'Expected Result',
      'Actual Result',
      'Attachments',
      'Attachment Title',
      'Attachment Description',
      'Date'
    ];
    const sampleDescription = 'Login button does not respond on click.';
    const sampleSteps = '1) Go to login page; 2) Enter credentials; 3) Click Login';
    const sampleExpected = 'User should be logged in and redirected to dashboard';
    const sampleActual = 'Nothing happens; no error shown';
    const rows = [
      header.join(','),
      [
        'Login button not working',
        sampleDescription,
        'Bug',
        'Bug Tracker',
        'dev@example.com',
        'Authentication',
        'Functional',
        'Open',
        'Medium',
        'Medium',
        'Chrome 122 on Windows 11',
        sampleSteps,
        sampleExpected,
        sampleActual,
        'https://example.com/screenshot1.png|https://example.com/video.mp4',
        'Screenshot|Video',
        'UI screenshot|Screen recording',
        '2026-01-23'
      ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')
    ];

    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'bugs-import-sample.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const statusColors = {
    'Open': 'bg-blue-50 text-blue-900 border border-blue-100',
    'In Progress': 'bg-orange-50 text-orange-900 border border-orange-100',
    'Fixed': 'bg-emerald-50 text-emerald-900 border border-emerald-100',
    'Retesting In Progress': 'bg-indigo-50 text-indigo-900 border border-indigo-100',
    'Closed': 'bg-green-100 text-green-950 border border-green-200',
    'Reopen': 'bg-red-50 text-red-950 border border-red-100',
    'Recurring Issue': 'bg-rose-50 text-rose-950 border border-rose-100',
    'On Hold': 'bg-yellow-50 text-yellow-950 border border-yellow-100',
    'Duplicate': 'bg-purple-50 text-purple-950 border border-purple-100',
    'Rejected': 'bg-gray-100 text-gray-950 border border-gray-200',
    'Not a Bug': 'bg-slate-100 text-slate-950 border border-slate-200',
    'Deferred': 'bg-amber-50 text-amber-950 border border-amber-100',
    'Deprecated': 'bg-gray-200 text-gray-950 border border-gray-300'
  };
  const displayStatus = (s) => (s === 'Retesting In Progress' ? 'QA in Progress' : (s === 'In Progress' ? 'Dev In Progress' : s));

  const severityColors = {
    'Low': 'bg-[#E3FCEF] text-[#006644]',
    'Medium': 'bg-[#FFF0B3] text-[#172B4D]',
    'High': 'bg-[#FFEBE6] text-[#DE350B]',
    'Critical': 'bg-[#FFEBE6] text-[#BF2600]'
  };

  const priorityColors = {
    'Low': 'bg-[#E3FCEF] text-[#006644]',
    'Medium': 'bg-[#FFF0B3] text-[#172B4D]',
    'High': 'bg-[#FFEBE6] text-[#DE350B]',
    'Critical': 'bg-[#FFEBE6] text-[#BF2600]'
  };

  const getAppForBug = useCallback((bug) => {
    const appId = bug?.application?._id || bug?.application;
    if (!appId) return null;
    return applications.find((a) => String(a._id) === String(appId)) || null;
  }, [applications]);



  const isBugReporter = useCallback((bug) => {
    if (!user) return false;
    const currentUserId = user._id?.toString() || user.id?.toString();
    if (!currentUserId) return false;

    // Handle different formats of reportedBy
    const reportedBy = bug?.reportedBy;
    if (!reportedBy) return false;

    // If reportedBy is an object, get its _id or id
    let reporterId;
    if (typeof reportedBy === 'object') {
      reporterId = reportedBy._id?.toString() || reportedBy.id?.toString();
    } else {
      // If it's a string, use it directly
      reporterId = reportedBy.toString();
    }

    if (!reporterId) return false;
    return String(reporterId) === String(currentUserId);
  }, [user]);

  const isAssignedDeveloper = useCallback((bug) => {
    if (!user) return false;
    const currentUserId = user._id?.toString() || user.id?.toString();
    if (!currentUserId) return false;

    // Handle different formats of assignedTo
    const assignedTo = bug?.assignedTo;
    if (!assignedTo) return false;

    // If assignedTo is an object, get its _id or id
    let assignedId;
    if (typeof assignedTo === 'object') {
      assignedId = assignedTo._id?.toString() || assignedTo.id?.toString();
    } else {
      // If it's a string, use it directly
      assignedId = assignedTo.toString();
    }

    if (!assignedId) return false;
    return String(assignedId) === String(currentUserId);
  }, [user]);

  const canDeveloperUpdateStatus = useCallback((bug) => {
    if (!user || user.role !== 'developer' || !bug) return false;
    const currentUserId = user._id?.toString() || user.id?.toString();
    if (!currentUserId) return false;
    if (isAssignedDeveloper(bug)) return true;
    const app = getAppForBug(bug);
    if (!app) return false;
    const ownerId = app.ownerId?._id || app.ownerId;
    const developerId = app.developerId?._id || app.developerId;
    const isOwner = ownerId && String(ownerId) === String(currentUserId);
    const isPrimaryDev = developerId && String(developerId) === String(currentUserId);
    const isTeamMember = Array.isArray(app.teamMembers) && app.teamMembers.some((m) => {
      const memberId = m.userId?._id || m.userId;
      return memberId && String(memberId) === String(currentUserId);
    });
    return isOwner || isPrimaryDev || isTeamMember;
  }, [getAppForBug, isAssignedDeveloper, user]);

  const canInlineEditStatus = useCallback((bug) => {
    if (!user || (user.role !== 'tester' && user.role !== 'developer')) return false;

    // For testers: any tester can edit status (including Closed bugs)
    if (user.role === 'tester') {
      return true;
    }

    // For developers: assigned developer OR application team members/owner can edit
    if (user.role === 'developer') {
      return canDeveloperUpdateStatus(bug);
    }

    return false;
  }, [canDeveloperUpdateStatus, user]);

  const canInlineEditSeverityPriority = useCallback((bug) => {
    if (!user || user.role !== 'tester') return false;
    // Only the tester who logged the bug can edit severity and priority
    return isBugReporter(bug);
  }, [isBugReporter, user]);

  const getStatusOptionsForBug = useCallback((bug, forceDisabled = false) => {
    if (!bug) return [];

    let options = [];
    if (user?.role === 'developer') {
      options = [
        { value: 'In Progress', label: 'In Progress', description: 'Developer is actively working on this bug' },
        { value: 'Fixed', label: 'Fixed', description: 'Developer has resolved the issue' },
        { value: 'On Hold', label: 'On Hold', description: 'Development is on hold due to dependencies or other issues' },
        { value: 'Rejected', label: 'Rejected', description: 'The bug is deemed invalid, not a real issue, or duplicate' },
        { value: 'Duplicate', label: 'Duplicate', description: 'This bug is a duplicate of another existing issue' },
        { value: 'Deferred', label: 'Deferred', description: 'Postponed (requires more info / too complex right now)' },
        { value: 'Not a Bug', label: 'Not a Bug', description: 'Expected behavior' },
        { value: 'Deprecated', label: 'Deprecated', description: 'Out of scope / no longer applicable' }
      ];
    } else {
      // Tester options - matching Bug Details page and instructions
      options = [
        { value: 'Open', label: 'Open' },
        { value: 'On Hold', label: 'On Hold', description: 'Bug is on hold and cannot proceed currently' },
        {
          value: 'Retesting In Progress',
          label: 'QA In Progress',
          disabled: bug.status !== 'Fixed' && bug.status !== 'Retesting In Progress',
          description: bug.status === 'Fixed' ? 'Start verifying the fix' : 'Currently in retesting'
        },
        { value: 'Reopen', label: 'Reopen', description: 'Requires proof URL' },
        {
          value: 'Closed',
          label: 'Closed',
          indicatorColor: '#10B981',
          disabled: (bug.status !== 'Fixed' && bug.status !== 'Retesting In Progress'),
          description: (bug.status !== 'Fixed' && bug.status !== 'Retesting In Progress') ? 'Available after developer fixes the bug' : 'Close this bug'
        },
        {
          value: 'Recurring Issue',
          label: 'Recurring Issue',
          disabled: bug.status !== 'Closed',
          description: bug.status === 'Closed' ? 'Mark as recurring — re-enters the workflow' : 'Only available for Closed bugs'
        }
      ];

      // Current logic says hide developer statuses for testers in the list selection.
      // But if the bug is currently in a developer status, we must show it in the list (disabled).
      const devStatuses = ['In Progress', 'Fixed', 'Rejected', 'Duplicate', 'Deferred', 'Not a Bug', 'Deprecated'];
      if (devStatuses.includes(bug.status)) {
        options.unshift({ value: bug.status, label: displayStatus(bug.status), disabled: true, description: 'Developer action required' });
      }
    }

    if (forceDisabled) {
      return options.map(o => ({ ...o, disabled: true }));
    }
    return options;
  }, [user?.role]);

  const getSeverityOptionsForBug = useCallback((bug, forceDisabled = false) => {
    const options = [
      { value: 'Low', label: 'Low' },
      { value: 'Medium', label: 'Medium' },
      { value: 'High', label: 'High' },
      { value: 'Critical', label: 'Critical' },
    ];
    if (forceDisabled) {
      return options.map(o => ({ ...o, disabled: true }));
    }
    return options;
  }, []);

  const getPriorityOptionsForBug = useCallback((bug, forceDisabled = false) => {
    const options = [
      { value: 'Low', label: 'Low' },
      { value: 'Medium', label: 'Medium' },
      { value: 'High', label: 'High' },
    ];
    if (forceDisabled) {
      return options.map(o => ({ ...o, disabled: true }));
    }
    return options;
  }, []);

  const getInlineEditorKey = useCallback((bugId, field) => `${bugId}:${field}`, []);

  const canViewInlineEditor = useCallback((bug, field = null) => {
    if (!user || !bug) return false;

    // Testers can always view/interact with status inline editor
    if (user.role === 'tester' && field === 'status') return true;

    // Reporters and Assignees can always view/interact with the pills
    if (isBugReporter(bug) || isAssignedDeveloper(bug)) return true;

    // Check if user belongs to the application team
    const app = getAppForBug(bug);
    if (!app) return false;

    const currentUserId = user._id?.toString() || user.id?.toString();
    const ownerId = app.ownerId?._id || app.ownerId;
    const developerId = app.developerId?._id || app.developerId;
    const isOwner = ownerId && String(ownerId) === String(currentUserId);
    const isPrimaryDev = developerId && String(developerId) === String(currentUserId);
    const isTeamMember = Array.isArray(app.teamMembers) && app.teamMembers.some((m) => {
      const memberId = m.userId?._id || m.userId;
      return memberId && String(memberId) === String(currentUserId);
    });

    return isOwner || isPrimaryDev || isTeamMember;
  }, [getAppForBug, isAssignedDeveloper, isBugReporter, user]);

  const openInlineEditor = useCallback((bug, field) => {
    // Check if user can at least view the editor (same application)
    if (!canViewInlineEditor(bug, field)) return;

    setActiveInlineEditor({ bugId: bug._id, field });
  }, [canViewInlineEditor]);

  const closeInlineEditor = useCallback(() => {
    setActiveInlineEditor(null);
  }, []);

  const updateBugInList = useCallback((updatedBug) => {
    setBugs((prev) => {
      const next = prev.map((b) => (String(b._id) === String(updatedBug._id) ? updatedBug : b));
      sessionStorage.setItem('bugListResults', JSON.stringify(next));
      return next;
    });
  }, []);

  const updateBugFieldInline = useCallback(async (bug, field, value) => {
    const key = `${bug._id}:${field}`;
    setInlineUpdating((prev) => ({ ...prev, [key]: true }));
    try {
      const { data } = await axios.put(`/api/bugs/${bug._id}`, { [field]: value });
      if (data?.data) {
        updateBugInList(data.data);

        // Dispatch event for other components to refresh (e.g., Dashboard)
        window.dispatchEvent(
          new CustomEvent('bugtracker:bug-updated', {
            detail: {
              bugId: bug._id,
              field,
              value,
              applicationId: data.data.application?._id || data.data.application || null
            }
          })
        );
      }
      toast.success(`${field.charAt(0).toUpperCase() + field.slice(1)} updated`);
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        toast.error('No Internet Connection. Please check your internet connection.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to update');
      }
    } finally {
      setInlineUpdating((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      closeInlineEditor();
    }
  }, [closeInlineEditor, updateBugInList]);

  const handleInlineStatusChange = useCallback(async (bug, newStatus) => {
    // Intercept 'Closed' or 'Reopen' for testers — show verification modal first
    if ((newStatus === 'Closed' || newStatus === 'Reopen') && user?.role === 'tester') {
      setPendingCloseBug(bug);
      setPendingVerificationStatusList(newStatus);
      setCloseBugVerificationLinkList('');
      setCloseBugVerificationNotesList('');
      setShowCloseBugModalList(true);
      closeInlineEditor();
      return;
    }
    await updateBugFieldInline(bug, 'status', newStatus);
  }, [updateBugFieldInline, closeInlineEditor, user?.role]);

  const handleCloseBugSubmitList = useCallback(async () => {
    if (!pendingCloseBug) return;
    const bug = pendingCloseBug;
    setShowCloseBugModalList(false);
    setPendingCloseBug(null);
    const key = `${bug._id}:status`;
    setInlineUpdating((prev) => ({ ...prev, [key]: true }));
    try {
      const payload = { status: pendingVerificationStatusList };
      const link = closeBugVerificationLinkList.trim();
      const notes = closeBugVerificationNotesList.trim();
      if (link) payload.proofUrl = link;
      if (notes) payload.proofDescription = notes;
      const { data } = await axios.put(`/api/bugs/${bug._id}`, payload);
      if (data?.data) {
        updateBugInList(data.data);
        window.dispatchEvent(
          new CustomEvent('bugtracker:bug-updated', {
            detail: {
              bugId: bug._id,
              field: 'status',
              value: pendingVerificationStatusList,
              applicationId: data.data.application?._id || data.data.application || null
            }
          })
        );
      }
      toast.success('Status updated');
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        toast.error('No Internet Connection. Please check your internet connection.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to update status');
      }
    } finally {
      setInlineUpdating((prev) => { const next = { ...prev }; delete next[`${bug._id}:status`]; return next; });
    }
  }, [pendingCloseBug, pendingVerificationStatusList, closeBugVerificationLinkList, closeBugVerificationNotesList, updateBugInList]);

  // --- Bulk status update (select multiple bugs and change status at once) ---
  // Rows the user can change get a checkbox next to the Bug ID; the header checkbox selects the whole page.
  // Selected bugs are updated through PUT /api/bugs/bulk/status (same rules as a single status change).
  const [selectedBugIds, setSelectedBugIds] = useState([]);
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkUpdating, setBulkUpdating] = useState(false);

  const canBulkSelectBug = useCallback(
    (bug) => viewMode !== 'archived' && canInlineEditStatus(bug),
    [canInlineEditStatus, viewMode]
  );

  const selectableBugIds = useMemo(
    () => bugs.filter(canBulkSelectBug).map(b => String(b._id)),
    [bugs, canBulkSelectBug]
  );

  // Keep the selection limited to bugs shown on the current page
  useEffect(() => {
    setSelectedBugIds(prev => {
      const next = prev.filter(id => selectableBugIds.includes(id));
      return next.length === prev.length ? prev : next;
    });
  }, [selectableBugIds]);

  const allPageSelected = selectableBugIds.length > 0 && selectableBugIds.every(id => selectedBugIds.includes(id));
  const somePageSelected = selectedBugIds.length > 0 && !allPageSelected;

  const toggleBugSelection = useCallback((bugId) => {
    const id = String(bugId);
    setSelectedBugIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  }, []);

  const toggleSelectAllOnPage = useCallback(() => {
    setSelectedBugIds(allPageSelected ? [] : selectableBugIds);
  }, [allPageSelected, selectableBugIds]);

  const clearBulkSelection = useCallback(() => {
    setSelectedBugIds([]);
    setBulkStatus('');
  }, []);

  const bulkStatusOptions = useMemo(
    () => getStatusOptionsForBug({ status: '' }).map(o => ({ value: o.value, label: o.label })),
    [getStatusOptionsForBug]
  );

  const handleBulkStatusUpdate = useCallback(async () => {
    if (!bulkStatus || selectedBugIds.length === 0) return;
    setBulkUpdating(true);
    try {
      const { data } = await axios.put('/api/bugs/bulk/status', { bugIds: selectedBugIds, status: bulkStatus });
      const label = displayStatus(bulkStatus);

      if (data.updatedCount > 0) {
        toast.success(`${data.updatedCount} ${data.updatedCount === 1 ? 'bug' : 'bugs'} moved to ${label}`);
      }
      if (data.skippedCount > 0) {
        const details = data.skipped
          .slice(0, 5)
          .map(s => `${s.bugId || 'Bug'}: ${s.reason}`)
          .join('\n');
        const more = data.skippedCount > 5 ? `\n…and ${data.skippedCount - 5} more` : '';
        toast.error(`${data.skippedCount} skipped\n${details}${more}`, { duration: 7000, style: { whiteSpace: 'pre-line' } });
      }

      window.dispatchEvent(new CustomEvent('bugtracker:bug-updated', { detail: { field: 'status', value: bulkStatus, bulk: true } }));
      clearBulkSelection();
      await fetchBugs(currentPage, pageSize);
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        toast.error('No Internet Connection. Please check your internet connection.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to update status');
      }
    } finally {
      setBulkUpdating(false);
    }
  }, [bulkStatus, selectedBugIds, clearBulkSelection, fetchBugs, currentPage, pageSize]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Close Bug Modal — shown when tester selects 'Closed' from inline status dropdown */}
      {showCloseBugModalList && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center" role="dialog" aria-modal="true" aria-labelledby="close-bug-modal-list-title">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => { setShowCloseBugModalList(false); setPendingCloseBug(null); }}
          />
          {/* Modal Card */}
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header */}
            <div className="px-6 pt-6 pb-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h2 id="close-bug-modal-list-title" className="text-base font-bold text-gray-900">{pendingVerificationStatusList === 'Reopen' ? `Reopen ${pendingCloseBug?.ticketType || 'Bug'}` : `Close ${pendingCloseBug?.ticketType || 'Bug'}`}</h2>
                  <p className="text-xs text-gray-500 mt-0.5">{pendingVerificationStatusList === 'Reopen' ? `Provide details for reopening ${(pendingCloseBug?.ticketType || 'Bug').toLowerCase()} (optional)` : `Provide verification details for ${(pendingCloseBug?.ticketType || 'Bug').toLowerCase()} (optional)`}</p>
                </div>
              </div>
            </div>
            {/* Body */}
            <div className="px-6 py-5 space-y-4">
              <div>
                <label htmlFor="close-bug-list-link" className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">
                  Verification Link
                </label>
                <input
                  id="close-bug-list-link"
                  type="url"
                  value={closeBugVerificationLinkList}
                  onChange={(e) => setCloseBugVerificationLinkList(e.target.value)}
                  placeholder="Enter link to screenshot, video, or test evidence (optional)"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all placeholder:text-gray-400 bg-gray-50 focus:bg-white"
                />
              </div>
              <div>
                <label htmlFor="close-bug-list-notes" className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">
                  Verification Notes
                </label>
                <textarea
                  id="close-bug-list-notes"
                  rows={3}
                  value={closeBugVerificationNotesList}
                  onChange={(e) => setCloseBugVerificationNotesList(e.target.value)}
                  placeholder={pendingVerificationStatusList === 'Reopen' ? '👉 Describe why the issue still persists' : `Describe how the ${(pendingCloseBug?.ticketType || 'Bug').toLowerCase()} fix was verified (optional)`}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all placeholder:text-gray-400 bg-gray-50 focus:bg-white resize-none"
                />
              </div>
            </div>
            {/* Footer */}
            <div className="px-6 pb-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => { setShowCloseBugModalList(false); setPendingCloseBug(null); }}
                className="px-4 py-2 text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCloseBugSubmitList}
                className="px-5 py-2 text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-md transition-colors shadow-sm"
              >
                {pendingVerificationStatusList === 'Reopen' ? `Reopen ${pendingCloseBug?.ticketType || 'Bug'}` : `Close ${pendingCloseBug?.ticketType || 'Bug'}`}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Header with Integrated Filters */}
        <div className="flex items-center gap-2 mb-4">
          <div className="flex-1 min-w-0 flex items-center gap-2 overflow-x-auto whitespace-nowrap pb-1 scrollbar-hide">
            <h1 className="text-lg font-bold text-gray-900 whitespace-nowrap mr-2">
              {viewMode === 'archived' ? 'Archived Bugs' : 'Bugs'}
            </h1>

            {/* Compact Search */}
            <div className="relative w-48 h-10 group flex-shrink-0">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setActiveSearchTerm(searchTerm);
                }}
                className="h-full"
              >
                <input
                  type="text"
                  placeholder="Search ID/Title"
                  value={searchTerm}
                  autoComplete="off"
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                  }}
                  className="block w-full h-full pl-10 pr-7 border-2 border-primary-200/60 rounded text-[13px] focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white placeholder-gray-400 transition-all font-medium shadow-sm"
                />
              </form>
            </div>
            {viewMode === 'active' && (user.role === 'tester' || user.role === 'developer') && (
              <div className="w-44">
                <ScrollableDropdown
                  value={typeFilter}
                  onChange={handleTypeChange}
                  isMulti={true}
                  options={[
                    { value: 'All', label: 'All' },
                    { value: 'Bug', label: 'Bug' },
                    { value: 'Observation', label: 'Observation' },
                    { value: 'Improvement', label: 'Improvement' }
                  ]}
                  placeholder="Ticket Type"
                  buttonHeightClassName="h-10"
                  buttonClassName="border-2 border-primary-200/60 bg-white shadow-sm hover:border-primary-400/50 text-gray-700 font-semibold"
                  maxMenuHeightPx={160}
                  maxMenuHeightClassName="max-h-40"
                />
              </div>
            )}
            <button
              onClick={clearFilters}
              disabled={!hasActiveFilters}
              className={`h-8 inline-flex items-center px-3 rounded shadow-sm text-xs font-bold transition-all flex-shrink-0 ${hasActiveFilters
                ? 'text-white bg-blue-600 hover:bg-blue-700 cursor-pointer'
                : 'text-gray-600 bg-gray-200 cursor-default'
                }`}
              title={hasActiveFilters ? 'Clear search & filters' : 'No filters applied'}
            >
              Clear
            </button>

            {/* Removed small 'Clear all' icon button to avoid duplication */}
          </div>

          <div className="flex items-center gap-2 flex-nowrap whitespace-nowrap flex-shrink-0 ml-auto">
            {/* Archive Toggle Button moved to Actions area */}
            <button
              onClick={() => {
                const nextMode = viewMode === 'active' ? 'archived' : 'active';
                setLoading(true);
                setBugs([]);
                
                // Clear the cache for the mode we are switching TO, 
                // ensures we don't show stale cached data from minutes ago
                sessionStorage.removeItem(`bugListResults_${nextMode}`);
                
                setViewMode(nextMode);
                setCurrentPage(1);
                clearFilters(true);
              }}
              className={`h-8 inline-flex items-center px-3 rounded shadow-sm text-xs font-bold transition-all flex-shrink-0 border-2 ${viewMode === 'archived'
                ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100 hover:border-amber-300 shadow-inner'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              title={viewMode === 'active' ? 'View Archived Bugs' : 'Return to Active Bugs'}
            >
              <div className="flex items-center gap-1.5">
                {viewMode === 'archived' ? (
                  <>
                    <BugAntIcon className="h-4 w-4 text-amber-600" />
                    <span>View Active Bugs</span>
                  </>
                ) : (
                  <>
                    <ArchiveBoxIcon className="h-4 w-4 text-gray-500" />
                    <span>Archived Bugs</span>
                  </>
                )}
              </div>
            </button>

            <button
              onClick={() => setShowColumnSelector(true)}
              className="h-8 w-8 inline-flex items-center justify-center border border-gray-300 rounded shadow-sm text-gray-700 bg-white hover:bg-gray-50 transition-all flex-shrink-0"
              title="Customize Columns"
            >
              <ViewColumnsIcon className="h-4 w-4" />
            </button>

            {viewMode === 'active' && (user.role === 'tester' || user.role === 'developer') && (
              <button
                onClick={openImport}
                className="h-8 inline-flex items-center px-3 border border-gray-300 rounded shadow-sm text-xs font-bold text-gray-700 bg-white hover:bg-gray-50 transition-all flex-shrink-0"
                title="Import Excel/CSV"
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-2 text-gray-500" />
                Import
              </button>
            )}

            <button
              onClick={() => openExport('xlsx')}
              className="h-8 inline-flex items-center px-3 border border-gray-300 rounded shadow-sm text-xs font-bold text-gray-700 bg-white hover:bg-gray-50 transition-all flex-shrink-0"
              title="Export Excel"
            >
              <ArrowUpTrayIcon className="h-4 w-4 mr-2 text-gray-500" />
              Export
            </button>

            <div className="relative">
              <button
                onClick={() => setShowOverflowMenu(!showOverflowMenu)}
                className="h-8 w-8 inline-flex items-center justify-center border border-gray-300 rounded shadow-sm text-gray-700 bg-white hover:bg-gray-50 transition-all flex-shrink-0"
                title="More Actions"
              >
                <EllipsisVerticalIcon className="h-5 w-5" />
              </button>

              {showOverflowMenu && (
                <>
                  <div
                    className="fixed inset-0 z-[100]"
                    onClick={() => setShowOverflowMenu(false)}
                  />
                  <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-[101]">
                    <div className="py-1" role="menu">
                      {viewMode === 'active' && (user.role === 'tester' || user.role === 'developer') && (
                        <button
                          onClick={() => {
                            setShowOverflowMenu(false);
                            openImport();
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                          role="menuitem"
                        >
                          <ArrowDownTrayIcon className="h-4 w-4 mr-3 text-gray-500" />
                          Import Excel/CSV
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setShowOverflowMenu(false);
                          openExport('xlsx');
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                        role="menuitem"
                      >
                        <ArrowUpTrayIcon className="h-4 w-4 mr-3 text-gray-500" />
                        Export Excel
                      </button>
                      <button
                        onClick={() => {
                          setShowOverflowMenu(false);
                          openExport('csv');
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                        role="menuitem"
                      >
                        <ArrowUpTrayIcon className="h-4 w-4 mr-3 text-gray-500" />
                        Export CSV
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {viewMode === 'active' && (user.role === 'tester' || user.role === 'developer') && (
              <button
                onClick={() => {
                  const targetPath = user.role === 'developer' ? '/developer/report-bug' : '/bugs/new';
                  navigate(targetPath);
                }}
                className="h-8 inline-flex items-center px-3 border border-transparent rounded shadow-sm text-xs font-bold text-white bg-red-600 hover:bg-red-700 transition-all shadow-red-200 flex-shrink-0"
              >
                Report Ticket
              </button>
            )}
          </div>
        </div>

        {showImportModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>

              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

              <div className={`inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle ${user.role === 'tester' ? 'sm:max-w-md max-h-[70vh]' : 'sm:max-w-xl'} sm:w-full`}>
                <div className={`bg-white ${user.role === 'tester' ? 'px-4 pt-4 pb-2 sm:p-5 sm:pb-3' : 'px-4 pt-5 pb-4 sm:p-6 sm:pb-4'}`}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl leading-6 font-bold text-gray-900">Import Bugs (Excel/CSV)</h3>
                    <button
                      type="button"
                      onClick={() => setShowImportModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                      title="Close"
                      disabled={importing}
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>

                  <div className={`${user.role === 'tester' ? 'max-h-[58vh] overflow-y-auto pr-1' : ''} space-y-6`}>
                    <div className="space-y-2">
                      <div className="text-xs font-bold text-gray-700">Select Application</div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Select Application
                        <span className="text-red-600 ml-1">*</span>
                      </label>
                      <ScrollableDropdown
                        value={importDefaultApplication}
                        onChange={(value) => setImportDefaultApplication(value)}
                        options={applications.map((app) => ({ value: app._id, label: `${app.name} (${app.version})` }))}
                        placeholder="Select application"
                        buttonHeightClassName="h-10"
                        maxMenuHeightPx={240}
                        maxMenuHeightClassName="max-h-60"
                      />
                      {importFieldErrors.application && (
                        <p className="mt-1 text-xs text-red-600">{importFieldErrors.application}</p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">Rows import into the selected application. If your file includes an Application column, it must match this selection.</p>
                    </div>

                    {user.role === 'tester' && (
                      <div className="space-y-2">
                        <div className="text-xs font-bold text-gray-700">Assignee (Developer)</div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Assignee (Developer)
                          <span className="text-red-600 ml-1">*</span>
                        </label>
                        <ScrollableDropdown
                          value={importAssignee}
                          onChange={(value) => setImportAssignee(value)}
                          options={importAssigneeOptions}
                          placeholder={importDefaultApplication ? 'Select developer assignee' : 'Select application first'}
                          buttonHeightClassName="h-10"
                          maxMenuHeightPx={240}
                          maxMenuHeightClassName="max-h-60"
                          disabled={!importDefaultApplication || importAssigneeOptions.length === 0}
                        />
                        {importFieldErrors.assignee && (
                          <p className="mt-1 text-xs text-red-600">{importFieldErrors.assignee}</p>
                        )}
                        <p className="mt-1 text-xs text-gray-500">Only developers from the selected application appear. Rows without an assignee will be assigned to this user.</p>
                      </div>
                    )}

                    <div className="space-y-2">
                      <div className="text-xs font-bold text-gray-700">Upload File</div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        CSV/Excel File
                        <span className="text-red-600 ml-1">*</span>
                      </label>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                        className="w-full"
                        disabled={importing}
                      />
                      {importFieldErrors.file && (
                        <p className="mt-1 text-xs text-red-600">{importFieldErrors.file}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2">
                        <button
                          type="button"
                          onClick={downloadSampleCsv}
                          disabled={importing}
                          className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded text-xs font-bold text-gray-700 bg-white hover:bg-gray-50"
                        >
                          Download Sample CSV
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowTemplateInfo(!showTemplateInfo)}
                          className="inline-flex items-center text-xs font-bold text-primary-600 hover:text-primary-700"
                        >
                          {showTemplateInfo ? 'Hide Template Columns' : 'View Template Columns'}
                        </button>
                      </div>
                      {showTemplateInfo && (
                        <ul className="mt-2 text-xs text-gray-600 list-disc pl-5 space-y-1">
                          <li>Bug Title*</li>
                          <li>Description*</li>
                          <li>Bug/Observation*</li>
                          <li>Application</li>
                          <li>Assigned To</li>
                          <li>Module*</li>
                          <li>Category, Severity, Priority</li>
                          <li>Environment</li>
                          <li>Steps to Reproduce</li>
                          <li>Expected Result, Actual Result</li>
                          <li>Attachments (URL), Attachment Title, Attachment Description</li>
                          <li>Date</li>
                          <li>Fields marked * are mandatory.</li>
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={importFromCsv}
                    disabled={importing}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-bold text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {importing ? 'Importing...' : 'Import'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowImportModal(false)}
                    disabled={importing}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-bold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}


        {/* Column Selector Panel - Right Side */}
        {showColumnSelector && (
          <div className="fixed inset-0 z-50 overflow-hidden" aria-labelledby="column-selector-title" role="dialog" aria-modal="true">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              aria-hidden="true"
              onClick={() => setShowColumnSelector(false)}
            ></div>

            {/* Panel */}
            <div className="fixed inset-y-0 right-0 flex max-w-full pl-10">
              <div className="w-screen max-w-sm">
                <div className="flex h-full flex-col bg-white shadow-xl">
                  {/* Header */}
                  <div className="bg-[#F8FAFC] px-4 py-3 border-b border-[#E5E7EB]">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-base leading-6 font-bold text-[#111827]" id="column-selector-title">
                          Show/Hide Columns
                        </h3>
                        <p className="mt-0.5 text-xs text-[#6B7280]">
                          Toggle columns on or off to customize your table view
                        </p>
                      </div>
                      <button
                        onClick={() => setShowColumnSelector(false)}
                        className="bg-transparent rounded-md text-gray-400 hover:text-gray-500 focus:outline-none transition-colors"
                      >
                        <span className="sr-only">Close</span>
                        <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 overflow-y-auto px-4 py-4 bg-white">
                    {/* Search Input */}
                    <div className="mb-3">
                      <input
                        type="text"
                        placeholder="Search columns..."
                        value={columnSearchTerm}
                        onChange={(e) => setColumnSearchTerm(e.target.value)}
                        className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                      />
                    </div>

                    {/* Column List */}
                    <div className="border border-gray-200 rounded-md">
                      {filteredColumnsForSelector.map((column, index) => (
                        <div
                          key={column.id}
                          draggable={!column.locked}
                          onDragStart={(e) => {
                            if (!column.locked) {
                              setDraggedItem(index);
                              e.dataTransfer.effectAllowed = 'move';
                            }
                          }}
                          onDragOver={(e) => {
                            if (!column.locked && draggedItem !== null) {
                              e.preventDefault();
                              e.dataTransfer.dropEffect = 'move';
                            }
                          }}
                          onDrop={(e) => {
                            if (!column.locked && draggedItem !== null) {
                              e.preventDefault();
                              const newOrder = [...filteredColumnsForSelector];
                              const draggedColumn = newOrder[draggedItem];
                              newOrder.splice(draggedItem, 1);
                              newOrder.splice(index, 0, draggedColumn);

                              // Save the new order
                              const orderIds = newOrder.map(col => col.id);
                              setColumnOrder(orderIds);
                              localStorage.setItem('bugListColumnOrder', JSON.stringify(orderIds));
                              setDraggedItem(null);
                            }
                          }}
                          onDragEnd={() => setDraggedItem(null)}
                          className={`flex items-center gap-2 px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors ${column.locked ? 'bg-gray-50' : ''
                            } ${draggedItem === index ? 'opacity-50' : ''}`}
                        >
                          {/* Drag Handle */}
                          <div className={`flex-shrink-0 ${column.locked ? 'opacity-30 cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'}`}>
                            <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 16 16">
                              <circle cx="4" cy="4" r="1.5" />
                              <circle cx="4" cy="8" r="1.5" />
                              <circle cx="4" cy="12" r="1.5" />
                              <circle cx="8" cy="4" r="1.5" />
                              <circle cx="8" cy="8" r="1.5" />
                              <circle cx="8" cy="12" r="1.5" />
                            </svg>
                          </div>

                          <div className="flex items-center flex-1 min-w-0">
                            <span className={`text-sm truncate ${column.locked ? 'text-gray-500' : 'text-gray-900'}`}>
                              {column.label}
                            </span>
                            {column.locked && (
                              <div className="flex items-center ml-2 text-xs text-gray-500 flex-shrink-0" title="Required column">
                                <LockClosedIcon className="h-3 w-3 mr-0.5" />
                                <span className="text-[10px]">Required</span>
                              </div>
                            )}
                          </div>

                          {/* Toggle Switch - Jira Size (16px height) */}
                          <button
                            type="button"
                            onClick={() => !column.locked && toggleColumn(column.id)}
                            disabled={column.locked}
                            className={`relative inline-flex h-4 w-8 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 ${column.locked
                              ? 'opacity-50 cursor-not-allowed bg-gray-300'
                              : isColumnVisible(column.id)
                                ? 'bg-primary-600'
                                : 'bg-gray-300'
                              }`}
                            role="switch"
                            aria-checked={isColumnVisible(column.id)}
                            aria-labelledby={`toggle-${column.id}`}
                          >
                            <span
                              className={`pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow-sm transition duration-200 ease-in-out ${isColumnVisible(column.id) ? 'translate-x-4' : 'translate-x-0.5'
                                }`}
                              style={{ marginTop: '2px' }}
                            />
                          </button>
                        </div>
                      ))}
                    </div>

                    {/* Quick Actions */}
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={selectAllColumns}
                        className="px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-md transition-colors"
                      >
                        Select All
                      </button>

                      <button
                        type="button"
                        onClick={resetToDefaultColumns}
                        className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                      >
                        Reset to Default
                      </button>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="bg-[#F8FAFC] px-4 py-3 border-t border-[#E5E7EB]">
                    <button
                      type="button"
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-1.5 bg-[#2563EB] text-sm font-medium text-white hover:bg-[#1D4ED8] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2563EB] transition-colors"
                      onClick={() => setShowColumnSelector(false)}
                    >
                      Done
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Export Column Selection Modal */}
        {showExportModal && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center" role="dialog" aria-modal="true" aria-labelledby="export-modal-title">
            <div
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={() => setShowExportModal(false)}
            />
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden border border-gray-100">
              <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100">
                    <ArrowUpTrayIcon className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <h2 id="export-modal-title" className="text-sm font-bold text-gray-900">Customize Export Columns</h2>
                    <p className="text-[10px] text-gray-500">Select the columns you want to include in your {showExportModal.toUpperCase()} export</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowExportModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="px-6 py-5 max-h-[420px] overflow-y-auto custom-scrollbar">
                {/* Table Columns */}
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Table Columns</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mb-4">
                  {allColumns.filter(col => !col.nonExportable && !col.exportOnly).map(col => (
                    <label 
                      key={col.id} 
                      className={`flex items-center gap-2 p-1.5 rounded-lg cursor-pointer transition-all border ${
                        exportColumns.includes(col.id) 
                          ? 'bg-blue-50/40 border-blue-100' 
                          : 'hover:bg-gray-50 border-transparent'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={exportColumns.includes(col.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setExportColumns(prev => [...prev, col.id]);
                          } else {
                            setExportColumns(prev => prev.filter(c => c !== col.id));
                          }
                        }}
                        className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                      <span className={`text-[11px] font-medium ${exportColumns.includes(col.id) ? 'text-gray-900' : 'text-gray-600'}`}>
                        {col.label}
                      </span>
                    </label>
                  ))}
                </div>

                {/* Detail Fields (Export Only) */}
                <div className="border-t border-gray-100 pt-3">
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Detail Fields</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                    {allColumns.filter(col => col.exportOnly).map(col => (
                      <label 
                        key={col.id} 
                        className={`flex items-center gap-2 p-1.5 rounded-lg cursor-pointer transition-all border ${
                          exportColumns.includes(col.id) 
                            ? 'bg-purple-50/40 border-purple-100' 
                            : 'hover:bg-gray-50 border-transparent'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={exportColumns.includes(col.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setExportColumns(prev => [...prev, col.id]);
                            } else {
                              setExportColumns(prev => prev.filter(c => c !== col.id));
                            }
                          }}
                          className="h-3.5 w-3.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                        />
                        <span className={`text-[11px] font-medium ${exportColumns.includes(col.id) ? 'text-gray-900' : 'text-gray-600'}`}>
                          {col.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50/50 border-t border-gray-100 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setExportColumns(allColumns.filter(c => !c.nonExportable).map(c => c.id))}
                    className="text-[10px] font-bold text-blue-600 hover:text-blue-700"
                  >
                    Select All
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    onClick={() => setExportColumns(allColumns.filter(col => col.defaultVisible && !col.nonExportable).map(col => col.id))}
                    className="text-[10px] font-bold text-gray-500 hover:text-gray-600"
                  >
                    Defaults
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowExportModal(false)}
                    className="px-4 py-1.5 text-[11px] font-bold text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => exportBugs(showExportModal, exportColumns)}
                    disabled={exportColumns.length === 0}
                    className="px-5 py-1.5 text-[11px] font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Download {showExportModal.toUpperCase()}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Status Update Bar */}
        {selectedBugIds.length > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 shadow-sm">
            <span className="text-sm font-semibold text-blue-900">
              {selectedBugIds.length} {selectedBugIds.length === 1 ? 'bug' : 'bugs'} selected
            </span>
            <span className="text-sm text-blue-800">Change status to</span>
            <div className="w-48">
              <ScrollableDropdown
                value={bulkStatus}
                onChange={(value) => setBulkStatus(value)}
                options={bulkStatusOptions}
                placeholder="Select status"
                disabled={bulkUpdating}
                buttonHeightClassName="h-8"
                usePortal={true}
              />
            </div>
            <button
              type="button"
              onClick={handleBulkStatusUpdate}
              disabled={!bulkStatus || bulkUpdating}
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {bulkUpdating && <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />}
              {bulkUpdating ? 'Updating...' : 'Apply'}
            </button>
            <button
              type="button"
              onClick={clearBulkSelection}
              disabled={bulkUpdating}
              className="text-sm font-medium text-blue-700 hover:text-blue-900 disabled:opacity-50"
            >
              Clear selection
            </button>
          </div>
        )}

        {/* Bug Table */}
        <div className="bg-white shadow sm:rounded-lg">
          <div className="overflow-x-auto border border-gray-200 rounded-lg shadow-sm w-full">
            <div className="block w-full align-middle">
              <table className="w-full border-separate border-spacing-0">
                <thead className="bg-slate-50 sticky top-0 z-30">
                  <tr>
                    {orderedColumns.filter(col => isColumnVisible(col.id)).map(col => {
                      const isIdOrTitle = col.id === 'bugId' || col.id === 'title';
                      const isActions = col.id === 'actions';
                      const showFilter = !isIdOrTitle && !isActions;

                      let className = "px-3 py-2 text-center text-[13px] font-bold text-slate-700 border-b border-slate-200 whitespace-nowrap bg-slate-50";
                      let style = {};

                      if (col.id === 'bugId') {
                        className = "sticky left-0 z-40 px-3 py-2 text-center text-[13px] font-bold text-slate-700 border-b border-slate-200 whitespace-nowrap bg-slate-50";
                        style = { minWidth: '124px', width: '124px', maxWidth: '124px' };
                      } else if (col.id === 'title') {
                        className = "sticky left-[124px] z-40 px-3 py-2 text-center text-[13px] font-bold text-slate-700 border-b border-slate-200 whitespace-nowrap bg-slate-50 border-r-2 border-r-gray-200";
                        style = {
                          minWidth: '240px',
                          width: '30%',
                          maxWidth: '400px',
                          boxShadow: '2px 0 4px -2px rgba(0, 0, 0, 0.1)'
                        };
                      }

                      return (
                        <th
                          key={col.id}
                          scope="col"
                          className={className}
                          style={style}
                        >
                          <div className="flex items-center justify-center">
                            {col.id === 'bugId' && selectableBugIds.length > 0 && (
                              <input
                                type="checkbox"
                                className="mr-2 h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                checked={allPageSelected}
                                ref={el => { if (el) el.indeterminate = somePageSelected; }}
                                onChange={toggleSelectAllOnPage}
                                title={allPageSelected ? 'Clear selection' : 'Select all on this page'}
                                aria-label="Select all bugs on this page"
                              />
                            )}
                            <span>{col.label}</span>
                            {showFilter && (
                              ['createdDate', 'updatedDate', 'archivedAt'].includes(col.id) ? (
                                <DateColumnFilter
                                  columnId={col.id}
                                  label={col.label}
                                  value={filters[col.id] || ''}
                                  onChange={(newValue) => handleFilterChange({ target: { name: col.id, value: newValue } })}
                                />
                              ) : (
                                <MultiSelectColumnFilter
                                  columnId={col.id}
                                  label={col.label}
                                  options={getColumnFilterOptions(col.id)}
                                  selectedValues={Array.isArray(filters[col.id]) ? filters[col.id] : (filters[col.id] ? [filters[col.id]] : [])}
                                  onChange={(newValues) => handleFilterChange({ target: { name: col.id, value: newValues } })}
                                />
                              )
                            )}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {loading && (
                    <tr>
                      <td colSpan={visibleColumns.length} className="p-0">
                        <div className="flex items-center justify-center min-h-[320px]">
                          <div className="inline-flex items-center justify-center gap-3 text-gray-600">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                            <span className="text-sm font-medium">Loading...</span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  {!loading && bugs.length > 0 ? (
                    bugs.map(bug => (
                      <tr key={bug._id} className={`${selectedBugIds.includes(String(bug._id)) ? 'bg-blue-50' : ''} hover:bg-gray-50 transition-colors group`}>
                        {orderedColumns.filter(col => isColumnVisible(col.id)).map(col => {
                          switch (col.id) {
                            case 'bugId':
                              return (
                                <td
                                  key={col.id}
                                  className={`sticky left-0 z-30 px-3 py-1.5 whitespace-nowrap text-xs font-medium border-b border-gray-100 group-hover:bg-gray-50 ${selectedBugIds.includes(String(bug._id)) ? 'bg-blue-50' : 'bg-white'}`}
                                  style={{ minWidth: '124px', width: '124px', maxWidth: '124px' }}
                                >
                                  <div className="flex items-center justify-center">
                                  {selectableBugIds.length > 0 && (
                                    <input
                                      type="checkbox"
                                      className="mr-2 h-3.5 w-3.5 flex-shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-30"
                                      checked={selectedBugIds.includes(String(bug._id))}
                                      disabled={!canBulkSelectBug(bug)}
                                      onChange={() => toggleBugSelection(bug._id)}
                                      title={canBulkSelectBug(bug) ? 'Select for bulk status update' : 'You cannot change the status of this bug'}
                                      aria-label={`Select ${bug.bugId || 'bug'}`}
                                    />
                                  )}
                                  {bug.bugId ? (
                                    <Link
                                      to={`/bugs/${bug.bugId}`}
                                      className="flex items-center justify-center gap-1.5 text-blue-600 hover:text-blue-800 hover:underline"
                                    >
                                      {bug.ticketType === 'Observation' || bug.isObservation || bug.category === 'Observation' ? (
                                        <LightBulbIcon className="h-3.5 w-3.5 text-amber-600" title="Observation" />
                                      ) : bug.ticketType === 'Improvement' ? (
                                        <ArrowTrendingUpIcon className="h-3.5 w-3.5 text-green-600" title="Improvement" />
                                      ) : (
                                        <BugAntIcon className="h-3.5 w-3.5 text-rose-600" title="Bug" />
                                      )}
                                      <span>{bug.bugId}</span>
                                    </Link>
                                  ) : '-'}
                                  </div>
                                </td>
                              );

                            case 'title':
                              return (
                                <td
                                  key={col.id}
                                  className={`sticky left-[124px] z-30 px-3 py-1.5 border-b border-gray-100 border-r-2 border-r-gray-200 group-hover:bg-gray-50 ${selectedBugIds.includes(String(bug._id)) ? 'bg-blue-50' : 'bg-white'}`}
                                  style={{
                                    minWidth: '240px',
                                    width: '30%',
                                    maxWidth: '400px',
                                    boxShadow: '2px 0 4px -2px rgba(0, 0, 0, 0.1)'
                                  }}
                                >
                                  <div
                                    title={bug.title}
                                    className="text-[13px] text-gray-900 leading-snug cursor-default"
                                    style={{
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden'
                                    }}
                                  >
                                    {bug.title}
                                  </div>
                                </td>
                              );

                            case 'reportedBy':
                              return (
                                <td key={col.id} className="px-3 py-1.5 whitespace-nowrap text-xs text-gray-700 border-b border-gray-100 text-center">
                                  {bug.reportedBy?.name}
                                </td>
                              );

                            case 'application':
                              return (
                                <td key={col.id} className="px-3 py-1.5 whitespace-nowrap border-b border-gray-100 text-center">
                                  <Link
                                    to={`/applications/${bug.application?._id || bug.application}/timeline`}
                                    className="text-xs font-medium text-indigo-600 hover:text-indigo-900 hover:underline"
                                  >
                                    {bug.application?.name || bug.applicationName || 'N/A'}
                                    {bug.application?.version && ` (${bug.application.version})`}
                                  </Link>
                                  <div className="text-[11px] text-gray-500">
                                    {bug.module}
                                  </div>
                                </td>
                              );

                            case 'domain':
                              return (
                                <td key={col.id} className="px-3 py-1.5 whitespace-nowrap text-xs text-gray-700 border-b border-gray-100 text-center">
                                  {bug.domain?.name || '-'}
                                </td>
                              );

                            case 'category':
                              return (
                                <td key={col.id} className="px-3 py-1.5 whitespace-nowrap text-xs text-gray-700 border-b border-gray-100 text-center">
                                  {bug.category || 'Functional'}
                                </td>
                              );


                            case 'status':
                              return (
                                <td key={col.id} className="px-3 py-1.5 whitespace-nowrap text-center border-b border-gray-100">
                                  {viewMode === 'active' && canViewInlineEditor(bug, 'status') ? (
                                    <div className="relative inline-block">
                                      {activeInlineEditor?.bugId === bug._id && activeInlineEditor?.field === 'status' ? (
                                        canInlineEditStatus(bug) ? (
                                          <ScrollableDropdown
                                            value={bug.status}
                                            onChange={(value) => handleInlineStatusChange(bug, value)}
                                            options={getStatusOptionsForBug(bug)}
                                            disabled={!!inlineUpdating[getInlineEditorKey(bug._id, 'status')]}
                                            placeholder={displayStatus(bug.status)}
                                            usePortal={true}
                                            disableHorizontalScroll={true}
                                            open={true}
                                            onOpenChange={(open) => {
                                              if (!open) closeInlineEditor();
                                            }}
                                            buttonHeightClassName="h-6"
                                            maxMenuHeightPx={180}
                                            maxMenuHeightClassName="max-h-44"
                                            buttonClassName={`relative justify-center pl-2.5 pr-8 py-1 border-0 rounded-full text-[11px] font-semibold ${statusColors[bug.status] || 'bg-gray-100 text-gray-800'} overflow-hidden whitespace-nowrap min-w-[120px] [&>svg]:absolute [&>svg]:right-2.5 [&>svg]:w-3 [&>svg]:h-3 [&>svg]:opacity-60 [&>div]:w-full [&>div]:justify-center [&>div>span]:text-current [&>div>span]:font-semibold`}
                                            menuClassName="z-[1000] whitespace-nowrap"
                                          />
                                        ) : (
                                          <ScrollableDropdown
                                            value={bug.status}
                                            onChange={() => { }}
                                            options={getStatusOptionsForBug(bug, true)}
                                            placeholder={displayStatus(bug.status)}
                                            usePortal={true}
                                            disableHorizontalScroll={true}
                                            open={true}
                                            onOpenChange={(open) => {
                                              if (!open) closeInlineEditor();
                                            }}
                                            buttonHeightClassName="h-6"
                                            maxMenuHeightPx={180}
                                            maxMenuHeightClassName="max-h-44"
                                            buttonClassName={`relative justify-center pl-2.5 pr-8 py-1 border-0 rounded-full text-[11px] font-semibold ${statusColors[bug.status] || 'bg-gray-100 text-gray-800'} overflow-hidden whitespace-nowrap min-w-[120px] [&>svg]:absolute [&>svg]:right-2.5 [&>svg]:w-3 [&>svg]:h-3 [&>svg]:opacity-60 [&>div]:w-full [&>div]:justify-center [&>div>span]:text-current [&>div>span]:font-semibold`}
                                            menuClassName="z-[1000] whitespace-nowrap"
                                          />
                                        )
                                      ) : (
                                        <button
                                          type="button"
                                          className={`relative inline-flex items-center justify-center pl-2.5 pr-8 py-1 rounded-full text-[11px] font-semibold ${statusColors[bug.status] || 'bg-gray-100 text-gray-800'} min-w-[120px] overflow-hidden whitespace-nowrap transition-all hover:ring-1 hover:ring-offset-1 hover:ring-blue-400`}
                                          onClick={() => openInlineEditor(bug, 'status')}
                                        >
                                          <span className="truncate">{displayStatus(bug.status)}</span>
                                          <ChevronDownIcon className="absolute right-2.5 h-3 w-3 opacity-60" aria-hidden="true" />
                                        </button>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="relative inline-block">
                                      <ScrollableDropdown
                                        value={bug.status}
                                        onChange={() => { }}
                                        options={getStatusOptionsForBug(bug, true)}
                                        placeholder={displayStatus(bug.status)}
                                        usePortal={true}
                                        buttonHeightClassName="h-6"
                                        maxMenuHeightPx={180}
                                        maxMenuHeightClassName="max-h-44"
                                        buttonClassName={`relative justify-center pl-2.5 pr-8 py-1 border-0 rounded-full text-[11px] font-semibold ${statusColors[bug.status] || 'bg-gray-100 text-gray-800'} overflow-hidden whitespace-nowrap min-w-[120px] opacity-60 cursor-not-allowed [&>svg]:absolute [&>svg]:right-2.5 [&>svg]:w-3 [&>svg]:h-3 [&>svg]:opacity-60 [&>div]:w-full [&>div]:justify-center [&>div>span]:text-current [&>div>span]:font-semibold`}
                                        menuClassName="z-[1000] overflow-y-auto whitespace-nowrap"
                                        disabled={true}
                                      />
                                    </div>
                                  )}
                                </td>
                              );

                            case 'severity':
                              return (
                                <td key={col.id} className="px-3 py-1.5 whitespace-nowrap text-center border-b border-gray-100">
                                  {viewMode === 'active' && canViewInlineEditor(bug, 'severity') ? (
                                    <div className="relative inline-block">
                                      {activeInlineEditor?.bugId === bug._id && activeInlineEditor?.field === 'severity' ? (
                                        canInlineEditSeverityPriority(bug) ? (
                                          <ScrollableDropdown
                                            value={bug.severity}
                                            onChange={(value) => updateBugFieldInline(bug, 'severity', value)}
                                            options={getSeverityOptionsForBug(bug)}
                                            disabled={!!inlineUpdating[getInlineEditorKey(bug._id, 'severity')]}
                                            placeholder={bug.severity}
                                            usePortal={true}
                                            disableHorizontalScroll={true}
                                            open={true}
                                            onOpenChange={(open) => {
                                              if (!open) closeInlineEditor();
                                            }}
                                            buttonHeightClassName="h-6"
                                            maxMenuHeightPx={160}
                                            maxMenuHeightClassName="max-h-40"
                                            buttonClassName={`text-xs font-medium border-0 ${severityColors[bug.severity] || 'bg-gray-100 text-gray-800'} overflow-hidden whitespace-nowrap w-[80px]`}
                                            menuClassName="z-50 whitespace-nowrap"
                                          />
                                        ) : (
                                          <ScrollableDropdown
                                            value={bug.severity}
                                            onChange={() => { }}
                                            options={getSeverityOptionsForBug(bug, true)}
                                            placeholder={bug.severity}
                                            usePortal={true}
                                            disableHorizontalScroll={true}
                                            open={true}
                                            onOpenChange={(open) => {
                                              if (!open) closeInlineEditor();
                                            }}
                                            buttonHeightClassName="h-6"
                                            maxMenuHeightPx={160}
                                            maxMenuHeightClassName="max-h-40"
                                            buttonClassName={`text-xs font-medium border-0 ${severityColors[bug.severity] || 'bg-gray-100 text-gray-800'} overflow-hidden whitespace-nowrap w-[80px]`}
                                            menuClassName="z-50 whitespace-nowrap"
                                          />
                                        )
                                      ) : (
                                        <button
                                          type="button"
                                          className={`relative inline-flex items-center justify-center pl-2.5 pr-6 py-0.5 rounded-full text-[11px] font-medium ${severityColors[bug.severity] || 'bg-gray-100 text-gray-800'} w-[90px] overflow-hidden whitespace-nowrap hover:ring-1 hover:ring-offset-1 hover:ring-gray-300 transition-all`}
                                          onClick={() => openInlineEditor(bug, 'severity')}
                                        >
                                          <span className="truncate">{bug.severity}</span>
                                          <ChevronDownIcon className="absolute right-2 h-3 w-3 opacity-60" aria-hidden="true" />
                                        </button>
                                      )}
                                    </div>
                                  ) : (
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${severityColors[bug.severity] || 'bg-gray-100 text-gray-800'}`}>
                                      {bug.severity}
                                    </span>
                                  )}
                                </td>
                              );

                            case 'priority':
                              return (
                                <td key={col.id} className="px-3 py-1.5 whitespace-nowrap text-center border-b border-gray-100">
                                  {viewMode === 'active' && canViewInlineEditor(bug, 'priority') ? (
                                    <div className="relative inline-block">
                                      {activeInlineEditor?.bugId === bug._id && activeInlineEditor?.field === 'priority' ? (
                                        canInlineEditSeverityPriority(bug) ? (
                                          <ScrollableDropdown
                                            value={bug.priority}
                                            onChange={(value) => updateBugFieldInline(bug, 'priority', value)}
                                            options={getPriorityOptionsForBug(bug)}
                                            disabled={!!inlineUpdating[getInlineEditorKey(bug._id, 'priority')]}
                                            placeholder={bug.priority}
                                            usePortal={true}
                                            disableHorizontalScroll={true}
                                            open={true}
                                            onOpenChange={(open) => {
                                              if (!open) closeInlineEditor();
                                            }}
                                            buttonHeightClassName="h-6"
                                            maxMenuHeightPx={140}
                                            maxMenuHeightClassName="max-h-36"
                                            buttonClassName={`text-xs font-medium border-0 ${priorityColors[bug.priority] || 'bg-gray-100 text-gray-800'} overflow-hidden whitespace-nowrap w-[80px]`}
                                            menuClassName="z-50 whitespace-nowrap"
                                          />
                                        ) : (
                                          <ScrollableDropdown
                                            value={bug.priority}
                                            onChange={() => { }}
                                            options={getPriorityOptionsForBug(bug, true)}
                                            placeholder={bug.priority}
                                            usePortal={true}
                                            disableHorizontalScroll={true}
                                            open={true}
                                            onOpenChange={(open) => {
                                              if (!open) closeInlineEditor();
                                            }}
                                            buttonHeightClassName="h-6"
                                            maxMenuHeightPx={140}
                                            maxMenuHeightClassName="max-h-36"
                                            buttonClassName={`text-xs font-medium border-0 ${priorityColors[bug.priority] || 'bg-gray-100 text-gray-800'} overflow-hidden whitespace-nowrap w-[80px]`}
                                            menuClassName="z-50 whitespace-nowrap"
                                          />
                                        )
                                      ) : (
                                        <button
                                          type="button"
                                          className={`relative inline-flex items-center justify-center pl-2.5 pr-6 py-0.5 rounded-full text-[11px] font-medium ${priorityColors[bug.priority] || 'bg-gray-100 text-gray-800'} w-[90px] overflow-hidden whitespace-nowrap hover:ring-1 hover:ring-offset-1 hover:ring-gray-300 transition-all`}
                                          onClick={() => openInlineEditor(bug, 'priority')}
                                        >
                                          <span className="truncate">{bug.priority}</span>
                                          <ChevronDownIcon className="absolute right-2 h-3 w-3 opacity-60" aria-hidden="true" />
                                        </button>
                                      )}
                                    </div>
                                  ) : (
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${priorityColors[bug.priority] || 'bg-gray-100 text-gray-800'}`}>
                                      {bug.priority}
                                    </span>
                                  )}
                                </td>
                              );

                            case 'assignedTo':
                              return (
                                <td key={col.id} className="px-3 py-1.5 whitespace-nowrap text-center border-b border-gray-100">
                                  {viewMode === 'active' && user.role === 'tester' ? (
                                    <div className="relative inline-block">
                                      {/* Assigned To Logic kept same, just styles updated */}
                                      {activeInlineEditor?.bugId === bug._id && activeInlineEditor?.field === 'assignedTo' ? (
                                        <ScrollableDropdown
                                          value={bug.assignedTo?._id || bug.assignedTo || ''}
                                          onChange={(value) => updateBugFieldInline(bug, 'assignedTo', value)}
                                          options={
                                            (() => {
                                              const app = getAppForBug(bug);
                                              if (!app) return [];
                                              const devIds = new Set();
                                              const addId = (val) => {
                                                if (!val) return;
                                                const id = val._id || val.id || val;
                                                if (id) devIds.add(String(id));
                                              };
                                              addId(app.developerId);
                                              addId(app.ownerId);
                                              if (Array.isArray(app.teamMembers)) {
                                                app.teamMembers.forEach(m => {
                                                  const role = m.role || (m.userId && m.userId.role);
                                                  if (role === 'DEV' || role === 'developer') {
                                                    addId(m.userId);
                                                  }
                                                });
                                              }
                                              return reporters
                                                .filter(r => devIds.has(String(r._id)))
                                                .map(r => ({ value: r._id, label: r.name }));
                                            })()
                                          }
                                          disabled={!!inlineUpdating[getInlineEditorKey(bug._id, 'assignedTo')]}
                                          placeholder={bug.assignedTo?.name || bug.application?.developerId?.name || bug.application?.developerName || 'Unassigned'}
                                          usePortal={true}
                                          disableHorizontalScroll={true}
                                          open={true}
                                          onOpenChange={(open) => {
                                            if (!open) closeInlineEditor();
                                          }}
                                          buttonHeightClassName="h-6"
                                          maxMenuHeightPx={220}
                                          maxMenuHeightClassName="max-h-56"
                                          buttonClassName="text-xs font-medium border-0 bg-gray-100 text-gray-800 overflow-hidden whitespace-nowrap w-[140px]"
                                          menuClassName="z-50 overflow-y-auto whitespace-nowrap"
                                        />
                                      ) : (
                                        <button
                                          type="button"
                                          className="relative inline-flex items-center justify-center pl-2.5 pr-6 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-800 w-[150px] overflow-hidden whitespace-nowrap hover:ring-1 hover:ring-offset-1 hover:ring-gray-300 transition-all"
                                          onClick={() => openInlineEditor(bug, 'assignedTo')}
                                        >
                                          <span className="truncate">
                                            {bug.assignedTo?.name || bug.application?.developerId?.name || bug.application?.developerName || 'Unassigned'}
                                          </span>
                                          <ChevronDownIcon className="absolute right-2 h-3 w-3 opacity-60" aria-hidden="true" />
                                        </button>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-800 max-w-[140px] truncate">
                                      {bug.assignedTo?.name || bug.application?.developerId?.name || bug.application?.developerName || 'Unassigned'}
                                    </span>
                                  )}
                                </td>
                              );

                            case 'createdDate':
                              return (
                                <td key={col.id} className="px-3 py-1.5 whitespace-nowrap text-xs text-gray-500 border-b border-gray-100 text-center">
                                  <div>{new Date(bug.createdAt).toLocaleDateString()}</div>
                                  <div className="text-[10px] text-gray-400 mt-0.5">
                                    {new Date(bug.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  </div>
                                </td>
                              );

                            case 'updatedDate':
                              return (
                                <td key={col.id} className="px-3 py-1.5 whitespace-nowrap text-xs text-gray-500 border-b border-gray-100 text-center">
                                  {bug.updatedAt && bug.updatedAt !== bug.createdAt ? (
                                    <>
                                      <div>{new Date(bug.updatedAt).toLocaleDateString()}</div>
                                      <div className="text-[10px] text-gray-400 mt-0.5">
                                        {new Date(bug.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                      </div>
                                    </>
                                  ) : (
                                    <span className="text-gray-400">—</span>
                                  )}
                                </td>
                              );

                            case 'archivedAt':
                              return (
                                <td key={col.id} className="px-3 py-1.5 whitespace-nowrap text-xs text-gray-500 border-b border-gray-100 text-center">
                                  {bug.archivedAt ? (
                                    <>
                                      <div>{new Date(bug.archivedAt).toLocaleDateString()}</div>
                                      <div className="text-[10px] text-gray-400 mt-0.5">
                                        {new Date(bug.archivedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                      </div>
                                    </>
                                  ) : (
                                    <span className="text-gray-400">—</span>
                                  )}
                                </td>
                              );

                            case 'archivedBy':
                              return (
                                <td key={col.id} className="px-3 py-1.5 whitespace-nowrap text-xs text-gray-700 border-b border-gray-100 text-center">
                                  {bug.archivedBy?.name || '-'}
                                </td>
                              );

                            case 'actions':
                              return (
                                <td key={col.id} className="px-3 py-1.5 whitespace-nowrap text-xs text-gray-500 border-b border-gray-100 text-center">
                                  {(user.role === 'tester' || user.role === 'developer') ? (
                                    <div className="flex items-center justify-center space-x-2">
                                      {viewMode === 'active' ? (
                                        <button
                                          onClick={() => handleArchive(bug._id)}
                                          className="text-indigo-600 hover:text-indigo-900 font-medium text-xs border border-indigo-200 rounded px-2 py-0.5 hover:bg-indigo-50 transition-colors"
                                          title="Archive Bug"
                                        >
                                          Archive
                                        </button>
                                      ) : (
                                        <>
                                          {user.role === 'developer' && (
                                            <button
                                              onClick={() => handleRestore(bug._id)}
                                              className="text-indigo-600 hover:text-indigo-900 font-medium text-xs border border-indigo-200 rounded px-2 py-0.5 hover:bg-indigo-50 transition-colors"
                                              title="Restore Bug"
                                            >
                                              Restore
                                            </button>
                                          )}
                                          {user.role === 'developer' && (
                                            <button
                                              onClick={() => handleDelete(bug._id)}
                                              className="text-red-600 hover:text-red-900 font-medium text-xs border border-red-200 rounded px-2 py-0.5 hover:bg-red-50 transition-colors"
                                              title="Delete Bug Permanently"
                                            >
                                              Delete
                                            </button>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  ) : (
                                    '-'
                                  )}
                                </td>
                              );

                            default:
                              return null;
                          }
                        })}
                      </tr>
                    ))
                  ) : !loading ? (
                    <tr>
                      <td colSpan={visibleColumns.length} className="p-0">
                        <div className="flex items-center justify-center min-h-[360px]">
                          <div className="flex flex-col items-center text-center">
                            <MagnifyingGlassIcon className="h-12 w-12 text-gray-300 mb-3" />
                            <h3 className="text-lg font-medium text-gray-900">
                              {activeSearchTerm ? `No records found for "${activeSearchTerm}"` : 'No records found'}
                            </h3>
                            <p className="mt-1 text-sm text-gray-500">
                              {activeSearchTerm
                                ? 'Try adjusting your search term or filters to find what you are looking for.'
                                : 'Try adjusting your filters to find what you are looking for.'}
                            </p>
                            {activeSearchTerm && (
                              <button
                                onClick={() => {
                                  setSearchTerm('');
                                  setActiveSearchTerm('');
                                }}
                                className="mt-4 text-sm font-medium text-primary-600 hover:text-primary-500"
                              >
                                Clear search
                              </button>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination Controls */}
          <div className="bg-white px-4 py-3 flex flex-wrap items-center justify-between gap-y-2 border-t border-gray-200">
            {/* Page size selector */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700 whitespace-nowrap">Page size:</label>
              <div className="w-24">
                <ScrollableDropdown
                  value={String(pageSize)}
                  onChange={(value) => {
                    setPageSize(parseInt(value));
                    setCurrentPage(1);
                  }}
                  options={[
                    { value: '10', label: '10' },
                    { value: '25', label: '25' },
                    { value: '50', label: '50' },
                    { value: '100', label: '100' },
                  ]}
                  placeholder={String(pageSize)}
                  buttonHeightClassName="h-8"
                  maxMenuHeightPx={200}
                  maxMenuHeightClassName="max-h-48"
                />
              </div>
              <span className="text-sm text-gray-500 whitespace-nowrap">
                {totalCount > 0 && (
                  <>Showing {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, totalCount)} of {totalCount}</>
                )}
              </span>
            </div>

            {/* Page navigation */}
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => fetchBugs(1, pageSize)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="First page"
                >
                  ⏮
                </button>
                <button
                  onClick={() => fetchBugs(Math.max(1, currentPage - 1), pageSize)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Previous page"
                >
                  ◀
                </button>

                {/* Page Numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => fetchBugs(pageNum, pageSize)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === pageNum
                        ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => fetchBugs(Math.min(totalPages, currentPage + 1), pageSize)}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Next page"
                >
                  ▶
                </button>
                <button
                  onClick={() => fetchBugs(totalPages, pageSize)}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Last page"
                >
                  ⏭
                </button>
              </nav>
            </div>
          </div>
        </div>
    </div>
  </div>
);
};

export default BugList;
